/**
 * 2026 Career Expansion: 144 additional careers wired into the main
 * `careerSeeds` array in `src/data/careers.ts`.
 *
 * Coverage:
 *  - Aviation & Aerospace (12)
 *  - Security, Intelligence & Compliance (14) -> Business / Public Sector
 *  - Mental Health & Therapy (14)
 *  - Creator Economy & Digital Independence (12) -> Marketing / Self-Employed
 *  - Green Energy & Sustainability (14)
 *  - Luxury, Wealth & High-Ticket Sales (10) -> Business
 *  - Human Behaviour, Coaching & Workplace Dev (12) -> Business / Self-Employed
 *  - Media Production, Film, Audio & Entertainment (12) -> Marketing
 *  - Logistics, Supply Chain & International Trade (12)
 *  - AI-Adjacent, Data & Automation Careers (12)
 *  - Beauty, Aesthetics & Personal Services (10)
 *  - Maritime, Offshore & Specialist Physical (10)
 *  - Extra Under-Served Professional Careers (10) -> Business / Marketing
 *
 * Slug collision audit (none of the 144 new slugs collide with the
 * existing 144 slugs at the time of authoring). All entries added as
 * distinct careers per the pragmatic-collision rule agreed with the
 * product owner; no existing entries were overwritten.
 *
 * Twenty priority careers receive specialisations and (where realistic)
 * `soloMeta` to match the depth bar of the original seeds. The remaining
 * 124 ship as concise, valid entries ready for later enrichment.
 */

import type { CareerSeed } from "./careers";

// ---------------------------------------------------------------
// Helpers - keep each seed compact without dropping required fields.
// ---------------------------------------------------------------

type SeedInput = Omit<CareerSeed, "specialisations" | "skillsNeeded"> & {
  specialisations?: CareerSeed["specialisations"];
  skillsNeeded?: CareerSeed["skillsNeeded"];
};

/** Compact constructor: supplies safe defaults for sparse seeds. */
const mk = (s: SeedInput): CareerSeed => ({
  specialisations: [],
  skillsNeeded: [],
  ...s,
});

// ---------------------------------------------------------------
// A. Aviation, Aerospace & Drones (12)
// ---------------------------------------------------------------

const AVIATION: CareerSeed[] = [
  // PRIORITY
  {
    name: "Air Traffic Controller",
    category: "Aviation and Aerospace",
    shortDescription:
      "Guide aircraft safely through UK airspace and around busy airports, making fast, life-critical decisions under pressure.",
    whyItFits:
      "You stay calm under pressure, think in 3D, and can hold a lot of information in your head at once.",
    startingSalary: "£37,000 (trainee)",
    midSalary: "£50,000 - £75,000",
    seniorSalary: "£100,000 - £130,000+",
    timeToEntry: "2 - 4 years (NATS training)",
    degreeRequired: "Not required - aptitude tests matter most",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Tower or radar centre, shift work",
    stability: "Very strong - regulated, safety-critical role",
    weights: { structure: 3, income: 2, people: 0, creativity: -1, freedom: -1 },
    skillsNeeded: ["Spatial awareness", "Decision-making under pressure", "Clear communication", "Concentration"],
    specialisations: [
      { title: "Aerodrome (Tower) Controller", shortDescription: "Manage take-offs, landings and ground movements at an airport.", typicalEntryRoute: "NATS or military ATC training", salaryRange: "£37,000 - £80,000" },
      { title: "Approach Controller", shortDescription: "Sequence aircraft arriving and departing within terminal airspace.", typicalEntryRoute: "Validated tower controller progression", salaryRange: "£55,000 - £100,000" },
      { title: "Area (En-Route) Controller", shortDescription: "Handle cruising aircraft across large blocks of UK airspace.", typicalEntryRoute: "Top of training pipeline at Swanwick / Prestwick", salaryRange: "£75,000 - £130,000+" },
    ],
  },
  mk({
    name: "Cabin Crew",
    category: "Aviation and Aerospace",
    shortDescription: "Look after passengers in the air - safety, service, and keeping the cabin running smoothly.",
    whyItFits: "You like people, travel, and shift work that's never the same two days running.",
    startingSalary: "£20,000 - £24,000",
    midSalary: "£25,000 - £32,000",
    seniorSalary: "£32,000 - £45,000",
    timeToEntry: "0 - 6 months (airline training)",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "High",
    workEnvironment: "Aircraft, irregular hours, frequent travel",
    stability: "Cyclical with travel demand",
    weights: { people: 3, freedom: 1, structure: 0, income: -1, creativity: 0 },
    skillsNeeded: ["Customer service", "Calm under pressure", "Teamwork", "Stamina"],
  }),
  mk({
    name: "Aircraft Engineer",
    category: "Aviation and Aerospace",
    shortDescription: "Diagnose and repair complex aircraft systems to keep commercial fleets airworthy.",
    whyItFits: "You like solving mechanical puzzles where the stakes are real and standards are strict.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£45,000 - £55,000",
    seniorSalary: "£60,000 - £80,000",
    timeToEntry: "3 - 5 Years (apprenticeship + EASA Part-66)",
    degreeRequired: "Preferred - apprenticeship route common",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Hangar, ramp, sometimes outdoors",
    stability: "Strong, regulated trade",
    weights: { structure: 2, income: 2, freedom: 0, people: -1, creativity: 0 },
    skillsNeeded: ["Mechanical diagnosis", "Attention to detail", "EASA regulation knowledge", "Teamwork"],
  }),
  mk({
    name: "Aircraft Mechanic",
    category: "Aviation and Aerospace",
    shortDescription: "Hands-on maintenance, inspection and turnaround on aircraft - engines, airframes and systems.",
    whyItFits: "You enjoy working with your hands on serious kit and following exact procedures.",
    startingSalary: "£28,000 - £34,000",
    midSalary: "£35,000 - £42,000",
    seniorSalary: "£45,000 - £55,000",
    timeToEntry: "2 - 4 years (apprenticeship)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Hangar / ramp, often shift work",
    stability: "Steady - long-term industry demand",
    weights: { structure: 2, income: 1, people: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Manual dexterity", "Following procedures", "Mechanical aptitude", "Teamwork"],
  }),
  mk({
    name: "Aerospace Engineer",
    category: "Aviation and Aerospace",
    shortDescription: "Design and test aircraft, spacecraft, and propulsion systems for commercial, defence or space programmes.",
    whyItFits: "You enjoy serious engineering challenges and the rigour of safety-critical design.",
    startingSalary: "£32,000 - £40,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£65,000 - £95,000",
    timeToEntry: "3 - 5 Years (degree + grad scheme)",
    degreeRequired: "Yes - engineering degree",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, lab, test facility",
    stability: "Strong in defence, growing in space",
    weights: { structure: 2, income: 2, creativity: 1, people: -1, freedom: 0 },
    skillsNeeded: ["CAD / simulation", "Mathematics", "Systems thinking", "Technical writing"],
  }),
  mk({
    name: "Flight Dispatcher",
    category: "Aviation and Aerospace",
    shortDescription: "Plan flights - fuel, routing, weather and weight balance - working with crew before every departure.",
    whyItFits: "You're organised, calm with numbers, and like coordinated work behind the scenes.",
    startingSalary: "£25,000 - £30,000",
    midSalary: "£32,000 - £38,000",
    seniorSalary: "£40,000 - £55,000",
    timeToEntry: "6 - 18 months (dispatcher licence)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Operations centre, shift work",
    stability: "Steady, tied to airline operations",
    weights: { structure: 3, income: 0, people: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Numeracy", "Weather literacy", "Communication", "Multi-tasking"],
  }),
  mk({
    name: "Airport Operations Manager",
    category: "Aviation and Aerospace",
    shortDescription: "Run the day-to-day machine of an airport - safety, schedules, teams and passenger flow.",
    whyItFits: "You can lead operational teams and hold dozens of moving parts at once.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£45,000 - £55,000",
    seniorSalary: "£60,000 - £80,000",
    timeToEntry: "5+ years operational background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Airport terminal & airside",
    stability: "Stable, recovering post-pandemic",
    weights: { people: 2, structure: 2, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Leadership", "Crisis management", "Aviation regulation", "Stakeholder management"],
  }),
  mk({
    name: "Drone Operator",
    category: "Aviation and Aerospace",
    shortDescription: "Pilot commercial drones for survey, inspection, film, agriculture or emergency services work.",
    whyItFits: "You want a hands-on tech career outdoors with low entry barrier and freelance options.",
    startingSalary: "£25,000 - £32,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£45,000 - £70,000",
    timeToEntry: "1 - 3 months (CAA A2 / GVC)",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Outdoor sites across the UK",
    stability: "Growing - inspection and survey demand",
    weights: { freedom: 2, creativity: 1, structure: 0, people: 0, income: 0 },
    skillsNeeded: ["CAA regulations", "Spatial awareness", "Photography basics", "Risk assessment"],
    selfEmployable: true,
  }),
  mk({
    name: "Drone Surveyor",
    category: "Aviation and Aerospace",
    shortDescription: "Use drones for aerial surveying, mapping and 3D modelling - construction, infrastructure and environmental work.",
    whyItFits: "You want a niche tech-meets-outdoor role with growing demand.",
    startingSalary: "£30,000 - £38,000",
    midSalary: "£40,000 - £50,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "1 - 2 years (GVC + survey training)",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Mixed - site work plus desk-based processing",
    stability: "Strong growth in construction and renewables",
    weights: { structure: 1, income: 1, freedom: 1, people: 0, creativity: 1 },
    skillsNeeded: ["Photogrammetry", "GIS basics", "CAA compliance", "Reporting"],
    selfEmployable: true,
  }),
  mk({
    name: "Aviation Safety Officer",
    category: "Aviation and Aerospace",
    shortDescription: "Audit airline and airport operations against safety rules and lead investigations when something goes wrong.",
    whyItFits: "You're detail-driven, methodical, and want to make a measurable safety impact.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£60,000 - £85,000",
    timeToEntry: "3+ years aviation experience",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Mixed - office, hangar, site visits",
    stability: "Very strong - regulated role",
    weights: { structure: 3, income: 1, people: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Risk analysis", "Regulatory knowledge", "Report writing", "Investigation"],
  }),
  mk({
    name: "Baggage Operations Supervisor",
    category: "Aviation and Aerospace",
    shortDescription: "Run the baggage handling team at a major airport - tight turnarounds and zero margin for delay.",
    whyItFits: "You like leading operational teams and solving logistics problems in real time.",
    startingSalary: "£25,000 - £30,000",
    midSalary: "£30,000 - £35,000",
    seniorSalary: "£35,000 - £45,000",
    timeToEntry: "2 - 4 years operational background",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Airside, shift work",
    stability: "Steady at major airports",
    weights: { people: 2, structure: 2, income: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Team leadership", "Logistics", "Health & safety", "Calm under pressure"],
  }),
  mk({
    name: "Airport Security Officer",
    category: "Aviation and Aerospace",
    shortDescription: "Screen passengers and baggage and respond to security incidents at UK airports.",
    whyItFits: "You're trustworthy, calm under pressure, and want stable shift-based work without a degree.",
    startingSalary: "£22,000 - £26,000",
    midSalary: "£26,000 - £32,000",
    seniorSalary: "£32,000 - £40,000",
    timeToEntry: "0 - 6 months (vetting + training)",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Airport terminal, shift work",
    stability: "Strong - regulated security role",
    weights: { structure: 2, people: 1, income: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Vigilance", "Communication", "Procedure adherence", "Conflict de-escalation"],
  }),
];

// ---------------------------------------------------------------
// B. Security, Intelligence, Compliance & Fraud (14)
// (Routed to Business, Finance and Legal / Public Sector / Technology)
// ---------------------------------------------------------------

const SECURITY_COMPLIANCE: CareerSeed[] = [
  mk({
    name: "Intelligence Analyst",
    category: "Public Sector and Emergency Services",
    shortDescription: "Turn raw data, open-source material and reports into actionable intelligence for government or commercial clients.",
    whyItFits: "You're analytical, discreet, and like joining dots others miss.",
    startingSalary: "£30,000 - £35,000",
    midSalary: "£40,000 - £50,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office, security-cleared environments",
    stability: "Very strong - government and finance demand",
    weights: { structure: 2, income: 1, creativity: 0, people: -1, freedom: 0 },
    skillsNeeded: ["Research", "Analytical writing", "OSINT tools", "Critical thinking"],
  }),
  mk({
    name: "Fraud Investigator",
    category: "Business, Finance and Legal",
    shortDescription: "Investigate suspected fraud across banking, insurance or e-commerce and build evidence packs.",
    whyItFits: "You enjoy puzzle-solving with a paper trail and like seeing bad actors caught.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£38,000 - £48,000",
    seniorSalary: "£50,000 - £70,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, occasional site work",
    stability: "Very strong - rising financial crime caseloads",
    weights: { structure: 2, income: 1, people: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Investigation", "Evidence handling", "Interviewing", "Report writing"],
  }),
  mk({
    name: "Digital Forensics Investigator",
    category: "Technology",
    shortDescription: "Recover and analyse digital evidence from devices for criminal, civil or corporate cases.",
    whyItFits: "You like technical depth, careful methodology and high-stakes detail work.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£65,000 - £90,000",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Preferred - cyber / forensics qualifications matter",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Lab / office, secure environments",
    stability: "Strong - growing cybercrime caseloads",
    weights: { structure: 2, income: 2, creativity: 0, people: -1, freedom: 0 },
    skillsNeeded: ["Forensic tools (EnCase, FTK)", "Chain of custody", "Reverse engineering basics", "Court-ready reporting"],
  }),
  // PRIORITY
  {
    name: "Compliance Officer",
    category: "Business, Finance and Legal",
    shortDescription: "Make sure a business sticks to UK and international regulations - financial, data, anti-bribery and beyond.",
    whyItFits: "You're conscientious, like clear rules, and enjoy work that protects an organisation.",
    startingSalary: "£30,000 - £38,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£70,000 - £95,000+",
    timeToEntry: "1 - 3 years (often via finance / legal background)",
    degreeRequired: "Preferred - ICA / CISI qualifications widely valued",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office or hybrid, mostly desk-based",
    stability: "Very strong - mandatory function in regulated industries",
    weights: { structure: 3, income: 1, people: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Regulatory knowledge", "Risk assessment", "Clear writing", "Stakeholder management"],
    specialisations: [
      { title: "Financial Crime Compliance", shortDescription: "Anti-money laundering and sanctions screening in banks and fintechs.", typicalEntryRoute: "AML analyst -> compliance specialist", salaryRange: "£35,000 - £80,000" },
      { title: "Regulatory Compliance Manager", shortDescription: "Own the relationship with the FCA / PRA and run the firm's compliance programme.", typicalEntryRoute: "Senior compliance + ICA Diploma", salaryRange: "£60,000 - £110,000" },
      { title: "Data Protection / GDPR Compliance", shortDescription: "Embed GDPR and UK DPA requirements across products and operations.", typicalEntryRoute: "Privacy / legal background + IAPP certs", salaryRange: "£40,000 - £90,000" },
    ],
  },
  // PRIORITY
  {
    name: "Risk Analyst",
    category: "Business, Finance and Legal",
    shortDescription: "Identify, model and report on the risks an organisation carries - financial, operational, credit or market.",
    whyItFits: "You're analytical, comfortable with data, and like translating uncertainty into clear decisions.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £110,000+",
    timeToEntry: "1 - 3 years (often via grad scheme)",
    degreeRequired: "Preferred - finance / maths / economics",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office, hybrid, mostly desk",
    stability: "Strong - core function across banking and insurance",
    weights: { structure: 3, income: 2, creativity: 0, people: -1, freedom: 0 },
    skillsNeeded: ["Excel / SQL", "Statistical modelling", "Risk frameworks", "Clear written analysis"],
    specialisations: [
      { title: "Credit Risk Analyst", shortDescription: "Model the probability of default on loans and credit portfolios.", typicalEntryRoute: "Bank grad scheme + SQL/Python", salaryRange: "£35,000 - £85,000" },
      { title: "Operational Risk Analyst", shortDescription: "Assess process, people and technology risks across a firm.", typicalEntryRoute: "Audit / consulting background", salaryRange: "£35,000 - £75,000" },
      { title: "Market Risk Analyst", shortDescription: "Measure exposure to interest rates, FX and equity moves.", typicalEntryRoute: "Quant grad route at investment banks", salaryRange: "£45,000 - £110,000" },
    ],
  },
  mk({
    name: "Anti-Money Laundering Analyst",
    category: "Business, Finance and Legal",
    shortDescription: "Screen customers and transactions for money laundering and file Suspicious Activity Reports.",
    whyItFits: "You're detail-driven and want a stable career-change path into financial services.",
    startingSalary: "£28,000 - £33,000",
    midSalary: "£36,000 - £48,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "6 - 18 months",
    degreeRequired: "Preferred - ICA certificate often expected",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office or hybrid",
    stability: "Very strong - regulated, mandatory function",
    weights: { structure: 3, income: 0, people: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["KYC / CDD", "Transaction monitoring", "Investigative writing", "Sanctions awareness"],
  }),
  mk({
    name: "Information Security Manager",
    category: "Technology",
    shortDescription: "Own information security strategy across an organisation - policy, controls, audit and incident response.",
    whyItFits: "You combine technical depth with leadership and want a high-impact, well-paid security role.",
    startingSalary: "£55,000 - £70,000",
    midSalary: "£75,000 - £95,000",
    seniorSalary: "£100,000 - £150,000+",
    timeToEntry: "5+ years security background",
    degreeRequired: "Preferred - CISSP / CISM widely required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or hybrid",
    stability: "Very strong - critical role",
    weights: { structure: 2, income: 3, people: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["ISO 27001 / NIST", "Risk management", "Leadership", "Incident response"],
  }),
  mk({
    name: "Data Protection Officer",
    category: "Business, Finance and Legal",
    shortDescription: "Lead GDPR and UK data protection compliance across an organisation - the regulator-facing single point of contact.",
    whyItFits: "You enjoy regulation, privacy and acting as the conscience of a business.",
    startingSalary: "£40,000 - £50,000",
    midSalary: "£55,000 - £75,000",
    seniorSalary: "£80,000 - £120,000",
    timeToEntry: "3 - 5 Years legal / compliance / IT background",
    degreeRequired: "Preferred - legal background common",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or hybrid",
    stability: "Very strong - statutory role in many sectors",
    weights: { structure: 3, income: 2, people: 1, creativity: -1, freedom: 0 },
    skillsNeeded: ["UK GDPR", "Privacy impact assessments", "Stakeholder management", "Policy writing"],
  }),
  mk({
    name: "Health and Safety Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Keep workplaces safe - risk assessments, audits, training and incident investigation across sites.",
    whyItFits: "You're practical, persuasive and want a career-change path with broad demand.",
    startingSalary: "£28,000 - £34,000",
    midSalary: "£38,000 - £48,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "1 - 2 years (NEBOSH)",
    degreeRequired: "Preferred - NEBOSH widely accepted",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Mixed - office and site visits",
    stability: "Very strong - mandatory across most industries",
    weights: { structure: 2, people: 2, income: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Risk assessment", "Communication", "Training delivery", "Investigation"],
  }),
  mk({
    name: "Trading Standards Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Enforce consumer law and product safety regulations on behalf of local authorities.",
    whyItFits: "You care about fairness, like investigation work and want a stable public-service role.",
    startingSalary: "£28,000 - £33,000",
    midSalary: "£35,000 - £42,000",
    seniorSalary: "£45,000 - £55,000",
    timeToEntry: "2 - 4 years (CTSI qualification)",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Mixed - office, business premises",
    stability: "Strong - statutory function",
    weights: { structure: 2, people: 1, income: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Consumer law", "Investigation", "Communication", "Report writing"],
  }),
  mk({
    name: "Border Force Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Control passenger and freight movement at UK ports and airports - immigration, customs and security checks.",
    whyItFits: "You want a uniformed public-service role with structured progression and no degree requirement.",
    startingSalary: "£26,000 - £30,000",
    midSalary: "£32,000 - £38,000",
    seniorSalary: "£40,000 - £50,000",
    timeToEntry: "6 - 12 months (Home Office vetting & training)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Ports, airports, shift work",
    stability: "Very strong - Civil Service role",
    weights: { structure: 2, people: 1, income: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Procedure adherence", "Interviewing", "Vigilance", "Calm under pressure"],
  }),
  mk({
    name: "Probation Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Supervise offenders in the community, assess risk and help reduce reoffending.",
    whyItFits: "You want a values-driven public-sector role working directly with people.",
    startingSalary: "£28,000 - £33,000",
    midSalary: "£35,000 - £40,000",
    seniorSalary: "£42,000 - £52,000",
    timeToEntry: "2 - 3 years (PQiP programme)",
    degreeRequired: "Yes - PQiP requires degree",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, prison and community settings",
    stability: "Very strong - HMPPS role",
    weights: { people: 3, structure: 2, income: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Risk assessment", "Interviewing", "Resilience", "Report writing"],
  }),
  mk({
    name: "Security Consultant",
    category: "Business, Finance and Legal",
    shortDescription: "Advise organisations on physical, cyber or executive security threats and build mitigation plans.",
    whyItFits: "You combine technical or operational expertise with the ability to sell and advise.",
    startingSalary: "£40,000 - £50,000",
    midSalary: "£55,000 - £75,000",
    seniorSalary: "£90,000 - £150,000",
    timeToEntry: "5+ years security / intelligence background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Client sites, office, travel",
    stability: "Strong - private and corporate demand",
    weights: { structure: 1, income: 3, people: 1, creativity: 1, freedom: 1 },
    skillsNeeded: ["Threat assessment", "Consulting", "Stakeholder management", "Communication"],
    selfEmployable: true,
  }),
  mk({
    name: "Cyber Threat Intelligence Analyst",
    category: "Technology",
    shortDescription: "Track threat actors, analyse malware and produce intelligence reports for security teams and execs.",
    whyItFits: "You like deep technical research and writing for both technical and executive audiences.",
    startingSalary: "£40,000 - £50,000",
    midSalary: "£55,000 - £75,000",
    seniorSalary: "£80,000 - £120,000",
    timeToEntry: "2 - 4 years cyber background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote",
    stability: "Very strong - threat landscape growing",
    weights: { structure: 2, income: 2, creativity: 1, people: -1, freedom: 1 },
    skillsNeeded: ["OSINT", "Malware analysis", "MITRE ATT&CK", "Written reporting"],
  }),
];

// ---------------------------------------------------------------
// C. Mental Health, Therapy & Support (14)
// ---------------------------------------------------------------

const MENTAL_HEALTH: CareerSeed[] = [
  // PRIORITY
  {
    name: "CBT Therapist",
    category: "Mental Health and Therapy",
    shortDescription:
      "Deliver evidence-based cognitive behavioural therapy to people living with anxiety, depression, OCD, PTSD and more.",
    whyItFits:
      "You're empathetic, structured, and like working with proven frameworks to produce measurable change.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£45,000 - £55,000",
    seniorSalary: "£60,000 - £80,000+ (private + NHS combined)",
    timeToEntry: "3 - 5 Years (PG Dip + BABCP accreditation)",
    degreeRequired: "Regulated - core profession + BABCP route required",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "NHS service, private practice, online",
    stability: "Very strong - NHS Talking Therapies demand",
    weights: { people: 3, structure: 2, creativity: 0, income: 0, freedom: 1 },
    skillsNeeded: ["CBT protocols", "Active listening", "Clinical assessment", "Documentation"],
    specialisations: [
      { title: "High-Intensity CBT Therapist (NHS)", shortDescription: "Deliver step-3 CBT in NHS Talking Therapies services.", typicalEntryRoute: "PG Dip in CBT + supervised practice", salaryRange: "£43,000 - £55,000" },
      { title: "Private CBT Therapist", shortDescription: "Self-employed practice seeing clients privately or via EAP referrals.", typicalEntryRoute: "BABCP accredited + own caseload", salaryRange: "£60,000 - £100,000+" },
      { title: "Specialist CBT (Trauma / OCD)", shortDescription: "Sub-specialise in complex presentations like PTSD or OCD.", typicalEntryRoute: "Post-qualification training", salaryRange: "£50,000 - £75,000" },
    ],
    selfEmployable: true,
  },
  // PRIORITY
  {
    name: "Psychotherapist",
    category: "Mental Health and Therapy",
    shortDescription:
      "Provide long-form talking therapy to help clients understand and change patterns in their lives and relationships.",
    whyItFits:
      "You're a deep listener, curious about people, and want meaningful one-to-one work.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£38,000 - £55,000",
    seniorSalary: "£60,000 - £90,000+ (private)",
    timeToEntry: "4 - 7 years (UKCP or BACP route)",
    degreeRequired: "Regulated - accredited training required",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Private practice, NHS, charities",
    stability: "Strong - sustained mental-health demand",
    weights: { people: 3, creativity: 1, freedom: 2, structure: 1, income: 0 },
    skillsNeeded: ["Therapeutic relationship", "Reflective practice", "Ethics", "Self-care"],
    specialisations: [
      { title: "Integrative Psychotherapist", shortDescription: "Blend humanistic, psychodynamic and CBT approaches per client.", typicalEntryRoute: "UKCP / BACP accredited training", salaryRange: "£30,000 - £80,000" },
      { title: "Psychodynamic Psychotherapist", shortDescription: "Work in depth with unconscious patterns and early experience.", typicalEntryRoute: "BPC route, often 4+ years", salaryRange: "£35,000 - £85,000" },
      { title: "Couples and Family Therapist", shortDescription: "Work with relationships rather than individuals.", typicalEntryRoute: "Relate / family therapy training", salaryRange: "£32,000 - £75,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£500 - £2,000 (training top-ups, room hire, insurance)",
      timeToFirstClient: "2 - 4 months",
      monthOneIncome: "£200 - £1,500",
      yearOneIncome: "£15,000 - £35,000 part-time, £30,000 - £55,000 full-time",
      scaledIncome: "£60,000 - £100,000+ once a full caseload and niche are established",
      commonPitfalls: ["Trying to be 'a therapist for everyone' instead of a clear niche", "Under-pricing sessions", "Skipping supervision and burning out"],
      firstFiveClients: ["List on Counselling Directory, Psychology Today and BACP Find a Therapist", "Build relationships with GP surgeries and EAP providers", "Offer reduced-fee placements during training", "Write a niche article for a local publication", "Ask supervisors and peers for overflow referrals"],
      toolsAndCosts: ["Practice management software £20-40/mo", "Professional body fees £150-£300/yr", "Clinical supervision £60-£100/mo"],
      legalBasics: ["Register with ICO for data protection", "Professional liability insurance via professional body", "Written contract / GDPR-compliant client agreement"],
    },
  },
  mk({
    name: "Speech and Language Therapist",
    category: "Mental Health and Therapy",
    shortDescription: "Assess and treat children and adults with communication, language or swallowing difficulties.",
    whyItFits: "You combine clinical skill with creativity and patience in everyday sessions.",
    startingSalary: "£30,000 - £35,000",
    midSalary: "£37,000 - £45,000",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "3 - 4 years (BSc or MSc)",
    degreeRequired: "Regulated - HCPC registration required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "NHS, schools, private clinics",
    stability: "Very strong - long-standing demand",
    weights: { people: 3, structure: 2, creativity: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Clinical assessment", "Patience", "Family communication", "Care planning"],
    selfEmployable: true,
  }),
  // PRIORITY
  {
    name: "Mental Health Nurse",
    category: "Mental Health and Therapy",
    shortDescription:
      "Specialist nursing role focused on assessing, planning and delivering care for people with mental illness.",
    whyItFits:
      "You're empathetic, resilient and want a regulated career that genuinely changes lives.",
    startingSalary: "£28,407 (Band 5)",
    midSalary: "£35,392 - £42,618 (Band 6)",
    seniorSalary: "£43,742 - £58,972 (Band 7-8)",
    timeToEntry: "3 - 4 years (BSc Mental Health Nursing) or nursing apprenticeship",
    degreeRequired: "Regulated - NMC registration required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Wards, community, prisons, schools",
    stability: "Very strong - NHS workforce priority",
    weights: { people: 3, structure: 2, income: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Clinical assessment", "Risk assessment", "Resilience", "Therapeutic communication"],
    specialisations: [
      { title: "Community Mental Health Nurse", shortDescription: "Care for people in their homes via Community Mental Health Teams.", typicalEntryRoute: "Band 5 ward experience -> Band 6 community", salaryRange: "£35,000 - £50,000" },
      { title: "CAMHS Nurse", shortDescription: "Work in Child and Adolescent Mental Health Services.", typicalEntryRoute: "Mental health nursing + CAMHS rotation", salaryRange: "£35,000 - £55,000" },
      { title: "Forensic Mental Health Nurse", shortDescription: "Work in secure units and the justice system.", typicalEntryRoute: "Band 5 + forensic training", salaryRange: "£38,000 - £60,000" },
    ],
  },
  mk({
    name: "Addiction Counsellor",
    category: "Mental Health and Therapy",
    shortDescription: "Support people through alcohol, drug or behavioural addictions in 1:1 and group settings.",
    whyItFits: "You're non-judgmental, resilient, and want meaningful frontline work.",
    startingSalary: "£25,000 - £30,000",
    midSalary: "£32,000 - £38,000",
    seniorSalary: "£42,000 - £55,000",
    timeToEntry: "1 - 3 years (diploma + accreditation)",
    degreeRequired: "Preferred - accredited diploma sufficient for many roles",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Charity, NHS, private rehab",
    stability: "Strong - growing service demand",
    weights: { people: 3, creativity: 0, structure: 1, income: -1, freedom: 0 },
    skillsNeeded: ["Motivational interviewing", "Active listening", "Group facilitation", "Safeguarding"],
  }),
  mk({
    name: "Family Therapist",
    category: "Mental Health and Therapy",
    shortDescription: "Work with families and couples to understand patterns and improve relationships.",
    whyItFits: "You're systems-minded and energised by group dynamics rather than 1:1 only.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£45,000 - £55,000",
    seniorSalary: "£60,000 - £85,000+",
    timeToEntry: "4 - 6 years",
    degreeRequired: "Regulated - AFT-accredited training",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "NHS, charity, private practice",
    stability: "Strong",
    weights: { people: 3, creativity: 1, freedom: 1, structure: 1, income: 0 },
    skillsNeeded: ["Systemic theory", "Reflective practice", "Facilitation", "Cultural sensitivity"],
    selfEmployable: true,
  }),
  mk({
    name: "Behaviour Analyst",
    category: "Mental Health and Therapy",
    shortDescription: "Apply behavioural science (often ABA) to support autistic people and others with complex needs.",
    whyItFits: "You like measurable interventions and data-driven progress.",
    startingSalary: "£30,000 - £38,000",
    midSalary: "£40,000 - £50,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "2 - 4 years (BCBA training)",
    degreeRequired: "Preferred - BCBA certification widely required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Schools, clinics, in-home support",
    stability: "Growing",
    weights: { people: 2, structure: 3, creativity: 0, income: 0, freedom: 0 },
    skillsNeeded: ["Data collection", "Behavioural assessment", "Parent training", "Programme design"],
  }),
  mk({
    name: "SEND Support Worker",
    category: "Mental Health and Therapy",
    shortDescription: "Support children and young people with special educational needs and disabilities in schools and communities.",
    whyItFits: "You're patient, warm, and want meaningful, hands-on work without a degree barrier.",
    startingSalary: "£20,000 - £24,000",
    midSalary: "£25,000 - £30,000",
    seniorSalary: "£30,000 - £35,000",
    timeToEntry: "0 - 6 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Schools, families, community",
    stability: "Strong",
    weights: { people: 3, structure: 1, creativity: 1, income: -1, freedom: 0 },
    skillsNeeded: ["Patience", "Safeguarding", "Communication", "Adaptability"],
  }),
  mk({
    name: "Autism Support Specialist",
    category: "Mental Health and Therapy",
    shortDescription: "Provide tailored support to autistic adults or children at home, school or work.",
    whyItFits: "You enjoy individualised, relational work and creative problem-solving.",
    startingSalary: "£24,000 - £28,000",
    midSalary: "£30,000 - £38,000",
    seniorSalary: "£42,000 - £55,000",
    timeToEntry: "1 - 2 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Homes, schools, day services",
    stability: "Very strong - rising demand",
    weights: { people: 3, structure: 1, creativity: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Person-centred planning", "Sensory awareness", "Communication", "Patience"],
  }),
  mk({
    name: "Youth Worker",
    category: "Mental Health and Therapy",
    shortDescription: "Support young people through informal education, mentoring and community-based programmes.",
    whyItFits: "You connect easily with young people and care about long-term outcomes for them.",
    startingSalary: "£23,000 - £27,000",
    midSalary: "£29,000 - £35,000",
    seniorSalary: "£38,000 - £45,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred - JNC qualification for senior roles",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Youth centres, schools, streets, online",
    stability: "Strong - council and charity funded",
    weights: { people: 3, freedom: 1, creativity: 1, structure: 0, income: -1 },
    skillsNeeded: ["Group work", "Boundaries", "Safeguarding", "Programme design"],
  }),
  mk({
    name: "Art Therapist",
    category: "Mental Health and Therapy",
    shortDescription: "Use creative art-making as a therapeutic tool with children and adults facing emotional or mental health difficulties.",
    whyItFits: "You're creative and clinically interested - this role blends both worlds.",
    startingSalary: "£30,000 - £35,000",
    midSalary: "£37,000 - £45,000",
    seniorSalary: "£48,000 - £60,000",
    timeToEntry: "4 - 6 years (MA + HCPC)",
    degreeRequired: "Regulated - HCPC registration required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "NHS, schools, hospices, private",
    stability: "Strong, niche role",
    weights: { creativity: 3, people: 3, structure: 1, income: 0, freedom: 1 },
    skillsNeeded: ["Art-based intervention", "Therapeutic relationship", "Reflective practice", "Documentation"],
    selfEmployable: true,
  }),
  mk({
    name: "Music Therapist",
    category: "Mental Health and Therapy",
    shortDescription: "Use music to support therapeutic outcomes - dementia care, learning disabilities, mental health and more.",
    whyItFits: "You're a musician with a clinical heart - this combines both.",
    startingSalary: "£30,000 - £35,000",
    midSalary: "£37,000 - £45,000",
    seniorSalary: "£48,000 - £60,000",
    timeToEntry: "4 - 6 years (MA + HCPC)",
    degreeRequired: "Regulated - HCPC registration required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "NHS, schools, care homes, private",
    stability: "Strong",
    weights: { creativity: 3, people: 3, structure: 1, income: 0, freedom: 1 },
    skillsNeeded: ["Musical fluency", "Improvisation", "Clinical assessment", "Team working"],
    selfEmployable: true,
  }),
  mk({
    name: "Wellbeing Coach",
    category: "Mental Health and Therapy",
    shortDescription: "Help clients improve sleep, stress, mindset and habits - usually non-clinical 1:1 work.",
    whyItFits: "You're a great listener and motivator and want a self-employed path.",
    startingSalary: "£25,000 - £32,000",
    midSalary: "£35,000 - £50,000",
    seniorSalary: "£55,000 - £80,000+",
    timeToEntry: "6 - 18 months",
    degreeRequired: "Preferred - accredited coaching qualification",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Online, private practice, in-house",
    stability: "Growing but competitive",
    weights: { people: 3, freedom: 2, creativity: 1, income: 0, structure: 0 },
    skillsNeeded: ["Coaching frameworks", "Goal setting", "Listening", "Marketing"],
    selfEmployable: true,
  }),
  mk({
    name: "Rehabilitation Worker",
    category: "Mental Health and Therapy",
    shortDescription: "Help people recovering from injury, illness or addiction rebuild day-to-day independence.",
    whyItFits: "You want practical, hands-on people work with clear progress.",
    startingSalary: "£23,000 - £27,000",
    midSalary: "£28,000 - £34,000",
    seniorSalary: "£36,000 - £42,000",
    timeToEntry: "0 - 12 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Community, hospitals, clients' homes",
    stability: "Strong",
    weights: { people: 3, structure: 1, creativity: 0, income: -1, freedom: 0 },
    skillsNeeded: ["Care planning", "Empathy", "Resilience", "Goal setting"],
  }),
];

// ---------------------------------------------------------------
// D. Creator Economy & Digital Independence (12)
// ---------------------------------------------------------------

const CREATOR_ECONOMY: CareerSeed[] = [
  mk({
    name: "YouTuber",
    category: "Self-Employed and Business Owner",
    shortDescription: "Build an audience on YouTube and earn from ads, sponsorships, products and channel memberships.",
    whyItFits: "You like making things, you can stick at it for years, and you're OK being on camera.",
    startingSalary: "£0 - £5,000 (year 1)",
    midSalary: "£20,000 - £80,000",
    seniorSalary: "£100,000 - £1m+ (top creators)",
    timeToEntry: "12 - 36 months to meaningful income",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Home studio, mostly solo",
    stability: "Highly variable",
    weights: { creativity: 3, freedom: 3, income: 1, people: 0, structure: -1 },
    skillsNeeded: ["Storytelling", "Video editing", "Consistency", "Thumbnail / title craft"],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£500 - £3,000 (camera, mic, lighting, editing software)",
      timeToFirstClient: "Audience-first model - revenue lags content by 12-24 months",
      monthOneIncome: "£0",
      yearOneIncome: "£0 - £5,000",
      scaledIncome: "£40,000 - £500,000+ once channel is monetised at scale",
      commonPitfalls: ["Chasing trends instead of a clear niche", "Burning out after 20 videos with no traction", "Ignoring thumbnails and titles - the actual job"],
      firstFiveClients: ["Hit YPP eligibility (500 subs / 3,000 watch hours)", "Pitch one brand in your niche for a small sponsorship", "Sell a digital product (template, ebook, course) to existing viewers", "Join a creator network for ad rate uplift", "Add a Patreon / channel membership for superfans"],
      toolsAndCosts: ["Camera + lens £400-£1,500", "Editing software £20-£50/mo", "TubeBuddy / VidIQ £10-£30/mo"],
      legalBasics: ["Sole trader or Ltd", "Declare all platform income to HMRC", "Written brand-deal contracts"],
    },
  }),
  mk({
    name: "Podcaster",
    category: "Self-Employed and Business Owner",
    shortDescription: "Create and publish episodic audio content and monetise via sponsorship, premium subscriptions or products.",
    whyItFits: "You can hold a conversation, show up weekly for years, and care about your topic deeply.",
    startingSalary: "£0 - £3,000 (year 1)",
    midSalary: "£15,000 - £60,000",
    seniorSalary: "£80,000 - £500,000+",
    timeToEntry: "12 - 24 months to meaningful income",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Home studio, interviews remote or in person",
    stability: "Variable",
    weights: { creativity: 3, freedom: 3, people: 1, income: 0, structure: -1 },
    skillsNeeded: ["Interviewing", "Audio editing", "Consistency", "Distribution"],
    selfEmploymentOnly: true,
  }),
  mk({
    name: "Streamer",
    category: "Self-Employed and Business Owner",
    shortDescription: "Build a community via live streaming on Twitch, YouTube or Kick - games, just-chatting or specialist niches.",
    whyItFits: "You're comfortable improvising for hours and can keep an audience engaged in real time.",
    startingSalary: "£0 - £4,000 (year 1)",
    midSalary: "£15,000 - £60,000",
    seniorSalary: "£100,000 - £500,000+",
    timeToEntry: "18 - 36 months",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Home setup, evening / late hours common",
    stability: "Highly variable",
    weights: { creativity: 3, freedom: 3, people: 2, income: 0, structure: -2 },
    skillsNeeded: ["On-camera presence", "Community moderation", "Streaming tech", "Resilience"],
    selfEmploymentOnly: true,
  }),
  mk({
    name: "Newsletter Writer",
    category: "Marketing and Media",
    shortDescription: "Build a subscriber list around a topic and earn via paid subscriptions, sponsorships or related products.",
    whyItFits: "You enjoy writing, have something to say weekly, and want a low-cost owned-audience business.",
    startingSalary: "£0 - £5,000",
    midSalary: "£20,000 - £60,000",
    seniorSalary: "£80,000 - £300,000+",
    timeToEntry: "12 - 24 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Remote, solo",
    stability: "Variable",
    weights: { creativity: 3, freedom: 3, structure: 1, income: 0, people: 0 },
    skillsNeeded: ["Writing", "Audience research", "Consistency", "Email marketing"],
    selfEmployable: true,
  }),
  mk({
    name: "UGC Creator",
    category: "Marketing and Media",
    shortDescription: "Create user-generated-content-style ads for brands without needing your own large audience.",
    whyItFits: "You're comfortable on camera and want creator income without building a personal brand.",
    startingSalary: "£20,000 - £30,000",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£60,000 - £100,000+",
    timeToEntry: "0 - 6 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "High",
    workEnvironment: "Home, mostly solo",
    stability: "Growing fast",
    weights: { creativity: 3, freedom: 2, income: 0, people: 0, structure: -1 },
    skillsNeeded: ["Phone videography", "Hooks / scripting", "Brand communication", "Self-direction"],
    selfEmployable: true,
  }),
  mk({
    name: "Influencer Manager",
    category: "Marketing and Media",
    shortDescription: "Manage influencer relationships and campaigns on behalf of brands or talent agencies.",
    whyItFits: "You combine relationship skills with marketing knowhow and like fast-moving work.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£38,000 - £48,000",
    seniorSalary: "£55,000 - £80,000",
    timeToEntry: "1 - 3 years marketing background",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Agency or in-house, hybrid",
    stability: "Growing",
    weights: { people: 2, creativity: 2, income: 1, structure: 0, freedom: 1 },
    skillsNeeded: ["Negotiation", "Campaign planning", "Analytics", "Communication"],
  }),
  mk({
    name: "Creator Talent Manager",
    category: "Marketing and Media",
    shortDescription: "Represent creators - negotiate deals, manage diaries, build long-term commercial plans.",
    whyItFits: "You're commercial, well-connected and like managing other people's careers.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£55,000 - £75,000",
    seniorSalary: "£90,000 - £150,000+",
    timeToEntry: "3 - 5 Years industry background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Agency, travel for shoots and events",
    stability: "Strong - growing creator economy",
    weights: { people: 3, income: 3, structure: 0, creativity: 1, freedom: 1 },
    skillsNeeded: ["Negotiation", "Contracts", "Relationship management", "Commercial strategy"],
  }),
  mk({
    name: "Community Monetisation Specialist",
    category: "Marketing and Media",
    shortDescription: "Help businesses turn online communities into paid memberships, products and ongoing revenue.",
    whyItFits: "You combine community-building skills with commercial thinking.",
    startingSalary: "£30,000 - £38,000",
    midSalary: "£42,000 - £55,000",
    seniorSalary: "£65,000 - £85,000",
    timeToEntry: "2 - 4 years community / marketing background",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Remote",
    stability: "Growing",
    weights: { people: 2, creativity: 2, income: 1, structure: 0, freedom: 2 },
    skillsNeeded: ["Community design", "Membership pricing", "Onboarding flows", "Retention metrics"],
  }),
  mk({
    name: "TikTok Shop Specialist",
    category: "Marketing and Media",
    shortDescription: "Run live shopping, creator partnerships and content strategy for brands selling on TikTok Shop.",
    whyItFits: "You're early to platforms, like fast feedback loops and selling.",
    startingSalary: "£25,000 - £30,000",
    midSalary: "£35,000 - £48,000",
    seniorSalary: "£55,000 - £80,000",
    timeToEntry: "0 - 12 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote or hybrid",
    stability: "Growing fast in UK 2024+",
    weights: { creativity: 2, income: 1, freedom: 2, people: 1, structure: 0 },
    skillsNeeded: ["Live commerce", "Creator outreach", "Analytics", "Short-form content"],
    selfEmployable: true,
  }),
  mk({
    name: "Podcast Producer",
    category: "Marketing and Media",
    shortDescription: "Produce branded or independent podcasts - planning, editing, distribution and growth.",
    whyItFits: "You're a story-builder who likes audio craft and project management.",
    startingSalary: "£25,000 - £30,000",
    midSalary: "£35,000 - £45,000",
    seniorSalary: "£50,000 - £70,000",
    timeToEntry: "1 - 2 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Studio or remote",
    stability: "Growing",
    weights: { creativity: 3, structure: 1, freedom: 2, people: 1, income: 0 },
    skillsNeeded: ["Audio editing", "Story shaping", "Project management", "Distribution"],
    selfEmployable: true,
  }),
  mk({
    name: "Digital Community Builder",
    category: "Marketing and Media",
    shortDescription: "Build and grow online communities (Discord, Circle, Slack) on behalf of brands or your own product.",
    whyItFits: "You're a natural connector and care about people having a good experience together.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£40,000 - £55,000",
    seniorSalary: "£60,000 - £80,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote",
    stability: "Growing in SaaS and creator economy",
    weights: { people: 3, creativity: 2, freedom: 2, income: 0, structure: 0 },
    skillsNeeded: ["Programming events", "Moderation", "Onboarding flows", "Empathy"],
  }),
  mk({
    name: "Creator Partnerships Manager",
    category: "Marketing and Media",
    shortDescription: "Build long-term commercial relationships between platforms / brands and creators.",
    whyItFits: "You're commercial, well-organised and love working with creative people.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £100,000",
    timeToEntry: "3 - 5 Years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid, travel for events",
    stability: "Growing",
    weights: { people: 3, income: 2, creativity: 1, structure: 0, freedom: 1 },
    skillsNeeded: ["Partnership strategy", "Contract negotiation", "Analytics", "Communication"],
  }),
];

// ---------------------------------------------------------------
// E. Green Energy, Climate & Sustainability (14)
// ---------------------------------------------------------------

const GREEN_ENERGY: CareerSeed[] = [
  // PRIORITY
  {
    name: "Renewable Energy Engineer",
    category: "Green Energy and Sustainability",
    shortDescription:
      "Design, install and optimise renewable energy systems - solar, wind, hydro, storage and grid integration.",
    whyItFits:
      "You're a technical thinker who wants engineering work that genuinely contributes to the energy transition.",
    startingSalary: "£32,000 - £40,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£70,000 - £100,000+",
    timeToEntry: "3 - 5 Years (degree + grad scheme)",
    degreeRequired: "Yes - engineering degree usually required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Mixed - office, site, occasionally offshore",
    stability: "Very strong - 2030+ net zero pipeline",
    weights: { structure: 2, income: 2, creativity: 1, people: 0, freedom: 0 },
    skillsNeeded: ["Electrical / mechanical engineering", "System design", "Grid integration", "Sustainability literacy"],
    specialisations: [
      { title: "Solar PV Engineer", shortDescription: "Design utility-scale and commercial solar installations.", typicalEntryRoute: "Electrical degree + MCS / NICEIC training", salaryRange: "£32,000 - £75,000" },
      { title: "Wind Engineer", shortDescription: "Plan and operate onshore or offshore wind farms.", typicalEntryRoute: "Mechanical / electrical engineering + GWO", salaryRange: "£40,000 - £95,000" },
      { title: "Battery Storage Engineer", shortDescription: "Specify grid-scale BESS projects and EV charging infrastructure.", typicalEntryRoute: "Power systems + grid code knowledge", salaryRange: "£45,000 - £100,000" },
    ],
  },
  // PRIORITY
  {
    name: "Sustainability Consultant",
    category: "Green Energy and Sustainability",
    shortDescription:
      "Advise organisations on cutting emissions, hitting net-zero targets and meeting sustainability disclosure rules.",
    whyItFits:
      "You're analytical, persuasive and want commercial work with environmental impact.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£40,000 - £55,000",
    seniorSalary: "£65,000 - £90,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred - environmental / engineering / business",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Office, hybrid, client visits",
    stability: "Very strong - regulatory tailwind",
    weights: { structure: 2, income: 1, creativity: 1, people: 1, freedom: 1 },
    skillsNeeded: ["Carbon accounting", "ESG frameworks", "Stakeholder engagement", "Report writing"],
    specialisations: [
      { title: "Carbon Footprint Consultant", shortDescription: "Measure and reduce scope 1, 2 and 3 emissions for clients.", typicalEntryRoute: "Environmental degree + GHG Protocol", salaryRange: "£28,000 - £65,000" },
      { title: "ESG Reporting Consultant", shortDescription: "Help clients prepare TCFD, CSRD and SECR disclosures.", typicalEntryRoute: "Sustainability or finance background", salaryRange: "£40,000 - £85,000" },
      { title: "Sustainability Strategy Consultant", shortDescription: "Set net-zero strategy and roadmaps at executive level.", typicalEntryRoute: "Senior consultant + sector specialism", salaryRange: "£60,000 - £110,000" },
    ],
    selfEmployable: true,
  },
  // PRIORITY
  {
    name: "Wind Turbine Technician",
    category: "Green Energy and Sustainability",
    shortDescription:
      "Install, inspect and repair onshore and offshore wind turbines - one of the fastest-growing skilled trades in the UK.",
    whyItFits:
      "You're hands-on, comfortable at height and want a high-paying trade with no degree barrier.",
    startingSalary: "£30,000 - £35,000",
    midSalary: "£38,000 - £45,000",
    seniorSalary: "£50,000 - £65,000+ (offshore can be higher)",
    timeToEntry: "12 - 24 months (apprenticeship + GWO)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Wind farms (onshore or offshore), often shift rotations",
    stability: "Very strong - 2030 wind targets driving hiring",
    weights: { freedom: 2, income: 1, structure: 1, people: 0, creativity: 0 },
    skillsNeeded: ["Electrical & mechanical maintenance", "Working at height (GWO)", "Hydraulics", "Fault diagnosis"],
    specialisations: [
      { title: "Onshore Wind Technician", shortDescription: "Maintain onshore turbines across the UK, usually living closer to home.", typicalEntryRoute: "Apprenticeship + GWO basic safety training", salaryRange: "£30,000 - £50,000" },
      { title: "Offshore Wind Technician", shortDescription: "Work on offshore wind farms via boat or helicopter transfer.", typicalEntryRoute: "GWO + sea survival + offshore medical", salaryRange: "£45,000 - £80,000" },
      { title: "Wind Service Lead Technician", shortDescription: "Lead service teams across multiple sites.", typicalEntryRoute: "5+ years technician + leadership", salaryRange: "£50,000 - £75,000" },
    ],
  },
  mk({
    name: "Solar Panel Installer",
    category: "Green Energy and Sustainability",
    shortDescription: "Fit residential and commercial solar PV systems and battery storage to UK rooftops.",
    whyItFits: "You're a hands-on tradesperson and want to ride the green retrofit wave.",
    startingSalary: "£28,000 - £33,000",
    midSalary: "£35,000 - £45,000",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "6 - 18 months (MCS-registered training)",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Rooftops / domestic & commercial",
    stability: "Very strong - growth phase",
    weights: { freedom: 1, structure: 1, income: 0, people: 0, creativity: 0 },
    skillsNeeded: ["Working at height", "Electrical basics", "MCS standards", "Customer service"],
    selfEmployable: true,
  }),
  mk({
    name: "Environmental Consultant",
    category: "Green Energy and Sustainability",
    shortDescription: "Advise developers and businesses on environmental risk, planning and ecology requirements.",
    whyItFits: "You like fieldwork combined with report writing and want science-led work.",
    startingSalary: "£26,000 - £32,000",
    midSalary: "£35,000 - £48,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "1 - 3 years (degree + consultancy)",
    degreeRequired: "Preferred - environmental science / ecology",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Mixed - office, site visits",
    stability: "Strong - planning system demand",
    weights: { structure: 1, freedom: 1, creativity: 0, people: 1, income: 0 },
    skillsNeeded: ["EIA / HRA", "Fieldwork", "Report writing", "GIS"],
  }),
  mk({
    name: "Energy Assessor",
    category: "Green Energy and Sustainability",
    shortDescription: "Produce EPCs and assess buildings' energy performance for sale, let or grant applications.",
    whyItFits: "You want a flexible, self-employed role with low barrier to entry and steady local demand.",
    startingSalary: "£25,000 - £32,000",
    midSalary: "£35,000 - £42,000",
    seniorSalary: "£45,000 - £55,000",
    timeToEntry: "2 - 6 months (DEA training)",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "On the road, visiting properties",
    stability: "Steady - statutory demand",
    weights: { freedom: 2, structure: 1, income: 0, people: 1, creativity: 0 },
    skillsNeeded: ["EPC software", "Measurement", "Customer service", "Time management"],
    selfEmployable: true,
  }),
  mk({
    name: "ESG Analyst",
    category: "Green Energy and Sustainability",
    shortDescription: "Research and rate companies on environmental, social and governance factors for investors and ratings agencies.",
    whyItFits: "You combine finance and sustainability skills and like detailed research work.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £100,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred - finance / sustainability",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office, hybrid",
    stability: "Strong",
    weights: { structure: 2, income: 2, creativity: 0, people: 0, freedom: 0 },
    skillsNeeded: ["ESG frameworks", "Financial analysis", "Data analysis", "Report writing"],
  }),
  mk({
    name: "Carbon Consultant",
    category: "Green Energy and Sustainability",
    shortDescription: "Measure, manage and reduce greenhouse gas emissions for clients using GHG Protocol and SBTi.",
    whyItFits: "You want measurable climate impact via numbers and frameworks.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£48,000 - £60,000",
    seniorSalary: "£70,000 - £90,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office or hybrid",
    stability: "Very strong - regulatory pressure rising",
    weights: { structure: 2, income: 2, creativity: 0, people: 1, freedom: 0 },
    skillsNeeded: ["Carbon accounting", "GHG Protocol", "Data analysis", "Stakeholder engagement"],
  }),
  mk({
    name: "Sustainability Manager",
    category: "Green Energy and Sustainability",
    shortDescription: "Lead a company's sustainability strategy in-house - reporting, supply chain and stakeholder engagement.",
    whyItFits: "You're a strategist who likes commercial work with environmental purpose.",
    startingSalary: "£45,000 - £55,000",
    midSalary: "£60,000 - £75,000",
    seniorSalary: "£85,000 - £120,000",
    timeToEntry: "4 - 7 years",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Very strong",
    weights: { people: 2, structure: 2, income: 2, creativity: 1, freedom: 0 },
    skillsNeeded: ["Strategy", "ESG reporting", "Stakeholder engagement", "Leadership"],
  }),
  mk({
    name: "Retrofit Coordinator",
    category: "Green Energy and Sustainability",
    shortDescription: "Coordinate whole-house retrofits to PAS 2035 standards - insulation, heating and ventilation upgrades.",
    whyItFits: "You're organised and want a specialised green-construction role.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£45,000 - £55,000",
    seniorSalary: "£55,000 - £70,000",
    timeToEntry: "6 - 18 months (Retrofit Coordinator qual)",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Mixed - office, residential sites",
    stability: "Very strong - net zero retrofit pipeline",
    weights: { structure: 3, people: 1, income: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["PAS 2035", "Building physics", "Project coordination", "Stakeholder management"],
  }),
  mk({
    name: "Heat Pump Installer",
    category: "Green Energy and Sustainability",
    shortDescription: "Install air- and ground-source heat pumps under UK MCS standards and grant schemes.",
    whyItFits: "You're a heating engineer ready to retrain into the fastest-growing low-carbon trade.",
    startingSalary: "£30,000 - £35,000",
    midSalary: "£38,000 - £45,000",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "3 - 9 months (top-up training)",
    degreeRequired: "Not required - existing gas / plumbing background",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Residential and commercial properties",
    stability: "Very strong - government incentives",
    weights: { freedom: 1, structure: 1, income: 1, people: 0, creativity: 0 },
    skillsNeeded: ["Heating system design", "MCS standards", "Refrigerant handling", "Customer comms"],
    selfEmployable: true,
  }),
  mk({
    name: "Energy Trader",
    category: "Green Energy and Sustainability",
    shortDescription: "Buy and sell electricity, gas and carbon allowances on wholesale markets - fast, numerate work.",
    whyItFits: "You're sharp under pressure and want a high-paying role at the intersection of finance and energy.",
    startingSalary: "£45,000 - £60,000",
    midSalary: "£75,000 - £110,000",
    seniorSalary: "£130,000 - £250,000+ (with bonus)",
    timeToEntry: "2 - 5 years",
    degreeRequired: "Preferred - STEM / finance",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Trading floor, office",
    stability: "Cyclical but high upside",
    weights: { income: 3, structure: 1, creativity: 0, people: 0, freedom: -1 },
    skillsNeeded: ["Market analysis", "Risk management", "Python / VBA", "Pressure tolerance"],
  }),
  mk({
    name: "Climate Data Analyst",
    category: "Green Energy and Sustainability",
    shortDescription: "Analyse climate, emissions and weather data to support decisions across business, finance and policy.",
    whyItFits: "You're a data person who wants meaningful work on climate problems.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£48,000 - £62,000",
    seniorSalary: "£70,000 - £90,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred - STEM degree",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote",
    stability: "Strong - growing demand",
    weights: { structure: 2, income: 1, creativity: 0, people: 0, freedom: 1 },
    skillsNeeded: ["Python / R", "GIS", "Statistics", "Data visualisation"],
  }),
  mk({
    name: "Conservation Officer",
    category: "Green Energy and Sustainability",
    shortDescription: "Manage habitats, species and protected sites for charities, councils or government bodies.",
    whyItFits: "You want outdoor science-based work with environmental purpose.",
    startingSalary: "£24,000 - £29,000",
    midSalary: "£32,000 - £38,000",
    seniorSalary: "£40,000 - £50,000",
    timeToEntry: "1 - 3 years (often unpaid experience first)",
    degreeRequired: "Preferred - ecology / conservation",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Outdoor sites + office",
    stability: "Steady but funding-dependent",
    weights: { freedom: 2, structure: 1, creativity: 0, people: 1, income: -1 },
    skillsNeeded: ["Species ID", "Land management", "Fundraising basics", "Volunteer leading"],
  }),
];

// ---------------------------------------------------------------
// F. Luxury, Wealth & High-Ticket Sales (10) -> Business
// ---------------------------------------------------------------

const LUXURY_WEALTH: CareerSeed[] = [
  // PRIORITY
  {
    name: "Wealth Manager",
    category: "Business, Finance and Legal",
    shortDescription:
      "Manage investment portfolios and financial planning for high-net-worth individuals and families.",
    whyItFits:
      "You're commercially sharp, trustworthy, and like long-term relationship-led work.",
    startingSalary: "£45,000 - £60,000",
    midSalary: "£75,000 - £110,000",
    seniorSalary: "£150,000 - £400,000+ (including bonus)",
    timeToEntry: "3 - 7 years (CISI / CFA route)",
    degreeRequired: "Preferred - finance / economics",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office, client meetings",
    stability: "Strong - mass affluent demand rising",
    weights: { people: 2, income: 3, structure: 2, creativity: 0, freedom: 0 },
    skillsNeeded: ["Investment knowledge", "Tax planning", "Relationship building", "Regulatory knowledge"],
    specialisations: [
      { title: "Private Wealth Manager", shortDescription: "Look after clients with £1m+ in investable assets.", typicalEntryRoute: "CFA + 5 years at a private bank", salaryRange: "£80,000 - £250,000" },
      { title: "Investment Adviser", shortDescription: "Build and maintain portfolios for advised clients.", typicalEntryRoute: "Diploma in Investment Advice + experience", salaryRange: "£45,000 - £120,000" },
      { title: "Financial Planner", shortDescription: "Whole-of-life planning for affluent UK families.", typicalEntryRoute: "Level 4 Diploma in Regulated Financial Planning", salaryRange: "£50,000 - £150,000" },
    ],
    selfEmployable: true,
  },
  // PRIORITY
  {
    name: "Mortgage Advisor",
    category: "Business, Finance and Legal",
    shortDescription:
      "Help homebuyers, movers and remortgagors find the right mortgage and walk them through the process to completion.",
    whyItFits:
      "You're sales-minded, like financial puzzles, and want a strong-earning role without a degree.",
    startingSalary: "£25,000 - £35,000 (often + commission)",
    midSalary: "£40,000 - £60,000 OTE",
    seniorSalary: "£70,000 - £150,000 OTE (top brokers)",
    timeToEntry: "3 - 9 months (CeMAP qualification)",
    degreeRequired: "Not required - CeMAP needed",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, home, client meetings",
    stability: "Cyclical with property market",
    weights: { people: 3, income: 2, structure: 1, creativity: 0, freedom: 1 },
    skillsNeeded: ["Sales", "Mortgage products", "Affordability calculations", "Relationship management"],
    specialisations: [
      { title: "Employed Mortgage Advisor", shortDescription: "Salaried role at a bank, building society or broker network.", typicalEntryRoute: "CeMAP + employer training scheme", salaryRange: "£25,000 - £55,000" },
      { title: "Self-Employed Mortgage Broker", shortDescription: "Run your own book of business under a network.", typicalEntryRoute: "CeMAP + network sign-up", salaryRange: "£50,000 - £150,000" },
      { title: "Specialist (Buy-to-Let / Bridging)", shortDescription: "Focus on landlords, complex incomes or bridging finance.", typicalEntryRoute: "Mainstream broker + specialism", salaryRange: "£60,000 - £200,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£1,500 - £4,000 (CeMAP, CRM, network fees, compliance)",
      timeToFirstClient: "1 - 3 months once authorised",
      monthOneIncome: "£0 - £1,500",
      yearOneIncome: "£25,000 - £55,000",
      scaledIncome: "£80,000 - £200,000+ with a strong referral pipeline",
      commonPitfalls: ["No lead source plan - waiting for referrals", "Picking the wrong network", "Under-investing in CRM and admin"],
      firstFiveClients: ["Tell every estate agent in your area you're newly qualified", "Set up Google Business + reviews from existing contacts", "Partner with an accountant or solicitor for cross-referrals", "Create one local Facebook ad targeting movers", "Ask past employers if you can take a small panel of overflow leads"],
      toolsAndCosts: ["Network fee £200-£500/mo", "CRM (Smartr / 360 Lifecycle) £50-£100/mo", "Sourcing tools £30-£60/mo"],
      legalBasics: ["FCA authorisation via your network", "PI insurance", "Anti-money laundering registration"],
    },
  },
  mk({
    name: "Insurance Broker",
    category: "Business, Finance and Legal",
    shortDescription: "Find clients the right commercial or personal insurance and manage their cover through renewals and claims.",
    whyItFits: "You're sales-minded with attention to detail and want a stable, well-paying role without a degree.",
    startingSalary: "£25,000 - £32,000",
    midSalary: "£38,000 - £55,000",
    seniorSalary: "£60,000 - £100,000+",
    timeToEntry: "0 - 12 months (CII / Cert CII)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, client visits",
    stability: "Very strong",
    weights: { people: 2, income: 2, structure: 2, creativity: 0, freedom: 0 },
    skillsNeeded: ["Risk analysis", "Negotiation", "Customer service", "Regulation"],
    selfEmployable: true,
  }),
  mk({
    name: "Yacht Broker",
    category: "Business, Finance and Legal",
    shortDescription: "Represent buyers and sellers of yachts in private sales, often six- and seven-figure transactions.",
    whyItFits: "You're a relationship-led salesperson with niche industry interest and a global network.",
    startingSalary: "£30,000 - £40,000",
    midSalary: "£55,000 - £80,000",
    seniorSalary: "£100,000 - £250,000+ (commission heavy)",
    timeToEntry: "2 - 5 years industry exposure",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Marinas, boat shows, international travel",
    stability: "Cyclical - luxury demand sensitive",
    weights: { people: 3, income: 3, freedom: 2, creativity: 1, structure: 0 },
    skillsNeeded: ["Sales", "Vessel knowledge", "Negotiation", "Networking"],
    selfEmployable: true,
  }),
  mk({
    name: "Luxury Real Estate Agent",
    category: "Business, Finance and Legal",
    shortDescription: "Sell prime and super-prime residential property - £2m+ in London and Home Counties hotspots.",
    whyItFits: "You're polished, hyper-driven and want commission upside without a degree.",
    startingSalary: "£25,000 - £35,000 + commission",
    midSalary: "£60,000 - £100,000 OTE",
    seniorSalary: "£150,000 - £400,000+ OTE",
    timeToEntry: "1 - 3 years property background",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Office, viewings, evenings & weekends",
    stability: "Cyclical with prime market",
    weights: { people: 3, income: 3, freedom: 1, creativity: 1, structure: 0 },
    skillsNeeded: ["Sales", "Negotiation", "Marketing", "Client discretion"],
    selfEmployable: true,
  }),
  mk({
    name: "Private Jet Broker",
    category: "Business, Finance and Legal",
    shortDescription: "Arrange ad-hoc private aviation charters and aircraft sales for HNW and corporate clients.",
    whyItFits: "You're a 24/7 sales operator who likes high-pressure, high-margin deals.",
    startingSalary: "£28,000 - £40,000 + commission",
    midSalary: "£60,000 - £100,000 OTE",
    seniorSalary: "£150,000 - £350,000+ OTE",
    timeToEntry: "1 - 3 years aviation / sales background",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Office, 24/7 on-call",
    stability: "Cyclical",
    weights: { people: 3, income: 3, freedom: 1, structure: 0, creativity: 0 },
    skillsNeeded: ["Sales", "Aircraft knowledge", "Negotiation", "Discretion"],
  }),
  mk({
    name: "Luxury Brand Manager",
    category: "Marketing and Media",
    shortDescription: "Manage strategy, positioning and product launches for premium and luxury brands.",
    whyItFits: "You combine commercial discipline with strong aesthetic judgement.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£55,000 - £70,000",
    seniorSalary: "£80,000 - £130,000+",
    timeToEntry: "4 - 7 years marketing background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office, travel for events",
    stability: "Strong in established luxury houses",
    weights: { creativity: 2, people: 2, income: 2, structure: 1, freedom: 0 },
    skillsNeeded: ["Brand strategy", "Campaign management", "Trade marketing", "Aesthetic judgement"],
  }),
  mk({
    name: "Fine Art Dealer",
    category: "Business, Finance and Legal",
    shortDescription: "Buy, sell and broker paintings, sculpture and collectables for collectors, museums and investors.",
    whyItFits: "You combine art expertise with strong commercial instincts.",
    startingSalary: "£25,000 - £35,000",
    midSalary: "£45,000 - £75,000",
    seniorSalary: "£100,000 - £500,000+ (highly variable)",
    timeToEntry: "3 - 7 years gallery / auction background",
    degreeRequired: "Preferred - art history common",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Gallery, fairs, client meetings",
    stability: "Variable",
    weights: { creativity: 3, income: 2, people: 2, freedom: 2, structure: 0 },
    skillsNeeded: ["Connoisseurship", "Negotiation", "Discretion", "Network"],
    selfEmployable: true,
  }),
  mk({
    name: "Auction Specialist",
    category: "Design and Creative",
    shortDescription: "Catalogue and value items in a specialist department (paintings, watches, jewellery) at an auction house.",
    whyItFits: "You're a knowledgeable specialist who likes both research and dealmaking.",
    startingSalary: "£24,000 - £30,000",
    midSalary: "£35,000 - £48,000",
    seniorSalary: "£55,000 - £85,000",
    timeToEntry: "2 - 5 years auction house background",
    degreeRequired: "Preferred - art history, gemmology",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Auction house, travel for valuations",
    stability: "Steady",
    weights: { creativity: 3, structure: 1, people: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Cataloguing", "Valuation", "Client relations", "Specialist subject knowledge"],
  }),
  mk({
    name: "Executive Search Consultant",
    category: "Business, Finance and Legal",
    shortDescription: "Headhunt C-suite and senior leadership talent for clients across sectors - retained search work.",
    whyItFits: "You're a relationship-led salesperson who likes being trusted with serious decisions.",
    startingSalary: "£40,000 - £55,000",
    midSalary: "£70,000 - £120,000",
    seniorSalary: "£150,000 - £400,000+ (commission)",
    timeToEntry: "3 - 5 Years recruitment / industry background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office, client visits, occasional travel",
    stability: "Cyclical",
    weights: { people: 3, income: 3, structure: 0, creativity: 0, freedom: 1 },
    skillsNeeded: ["Research", "Networking", "Interviewing", "Negotiation"],
    selfEmployable: true,
  }),
];

// ---------------------------------------------------------------
// G. Human Behaviour, Coaching & Workplace Development (12)
// ---------------------------------------------------------------

const HUMAN_BEHAVIOUR: CareerSeed[] = [
  mk({
    name: "Career Coach",
    category: "Self-Employed and Business Owner",
    shortDescription: "Help individuals navigate career transitions, job search and progression through 1:1 coaching.",
    whyItFits: "You're a great listener with strong personal interest in work and identity.",
    startingSalary: "£25,000 - £35,000",
    midSalary: "£40,000 - £60,000",
    seniorSalary: "£70,000 - £120,000+ (private practice)",
    timeToEntry: "6 - 18 months (ICF / EMCC training)",
    degreeRequired: "Preferred - coaching qualification",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Online, private practice, occasionally in-house",
    stability: "Growing - career-change demand",
    weights: { people: 3, freedom: 2, creativity: 1, income: 0, structure: 0 },
    skillsNeeded: ["Coaching frameworks", "Active listening", "Marketing", "Programme design"],
    selfEmployable: true,
  }),
  mk({
    name: "Executive Coach",
    category: "Business, Finance and Legal",
    shortDescription: "Coach senior leaders on performance, leadership and decision-making - usually 6-12 month engagements.",
    whyItFits: "You have business experience and want to use it to develop other senior people.",
    startingSalary: "£45,000 - £65,000",
    midSalary: "£80,000 - £130,000",
    seniorSalary: "£150,000 - £300,000+",
    timeToEntry: "3 - 7 years (senior background + accreditation)",
    degreeRequired: "Preferred - ICF MCC widely valued",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Online or client offices",
    stability: "Strong - corporate leadership budgets",
    weights: { people: 3, income: 3, freedom: 2, creativity: 1, structure: 0 },
    skillsNeeded: ["Coaching mastery", "Business acumen", "Psychometrics", "Sales"],
    selfEmployable: true,
  }),
  mk({
    name: "Organisational Psychologist",
    category: "Business, Finance and Legal",
    shortDescription: "Apply psychology to workplaces - selection, performance, leadership, team and culture.",
    whyItFits: "You're a scientist-practitioner with strong people skills.",
    startingSalary: "£40,000 - £50,000",
    midSalary: "£55,000 - £75,000",
    seniorSalary: "£90,000 - £140,000+",
    timeToEntry: "5 - 7 years (BPS-accredited MSc + Stage 2)",
    degreeRequired: "Regulated - HCPC registration required",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Consulting, in-house, hybrid",
    stability: "Strong",
    weights: { people: 3, structure: 2, creativity: 1, income: 1, freedom: 1 },
    skillsNeeded: ["Psychometrics", "Research design", "Stakeholder management", "Consulting"],
    selfEmployable: true,
  }),
  mk({
    name: "Learning and Development Specialist",
    category: "Business, Finance and Legal",
    shortDescription: "Design and deliver employee training programmes across an organisation.",
    whyItFits: "You like teaching adults, designing learning and seeing measurable behaviour change.",
    startingSalary: "£30,000 - £37,000",
    midSalary: "£42,000 - £55,000",
    seniorSalary: "£65,000 - £85,000",
    timeToEntry: "2 - 4 years HR / training background",
    degreeRequired: "Preferred - CIPD valued",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong",
    weights: { people: 2, structure: 2, creativity: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Learning design", "Facilitation", "Stakeholder management", "Evaluation"],
  }),
  mk({
    name: "Talent Development Manager",
    category: "Business, Finance and Legal",
    shortDescription: "Lead talent strategy in-house - succession planning, leadership pipelines and capability building.",
    whyItFits: "You're a strategic HR practitioner who likes long-term workforce planning.",
    startingSalary: "£45,000 - £55,000",
    midSalary: "£60,000 - £75,000",
    seniorSalary: "£85,000 - £120,000",
    timeToEntry: "5 - 8 years HR background",
    degreeRequired: "Preferred - CIPD valued",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong",
    weights: { people: 3, structure: 2, income: 2, creativity: 1, freedom: 0 },
    skillsNeeded: ["Talent strategy", "Coaching", "Stakeholder management", "Analytics"],
  }),
  mk({
    name: "Corporate Trainer",
    category: "Self-Employed and Business Owner",
    shortDescription: "Deliver in-person and virtual training to corporate clients on leadership, sales, soft skills or product topics.",
    whyItFits: "You're a confident facilitator who likes variety and travel.",
    startingSalary: "£30,000 - £40,000",
    midSalary: "£45,000 - £65,000",
    seniorSalary: "£80,000 - £130,000+",
    timeToEntry: "2 - 5 years subject expertise",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Client offices, online",
    stability: "Variable - project-based",
    weights: { people: 3, freedom: 2, income: 1, creativity: 1, structure: 0 },
    skillsNeeded: ["Facilitation", "Content design", "Sales", "Stage presence"],
    selfEmployable: true,
  }),
  mk({
    name: "Leadership Development Consultant",
    category: "Business, Finance and Legal",
    shortDescription: "Design and deliver leadership programmes - exec teams, high-potential cohorts and culture change initiatives.",
    whyItFits: "You have senior people experience and like long-term, deep-impact work.",
    startingSalary: "£45,000 - £60,000",
    midSalary: "£75,000 - £110,000",
    seniorSalary: "£150,000 - £300,000+",
    timeToEntry: "5 - 8 years",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Client offices, online, travel",
    stability: "Strong",
    weights: { people: 3, income: 3, creativity: 2, freedom: 2, structure: 0 },
    skillsNeeded: ["Programme design", "Facilitation", "Coaching", "Sales"],
    selfEmployable: true,
  }),
  mk({
    name: "Employee Experience Manager",
    category: "Business, Finance and Legal",
    shortDescription: "Own the design of the day-to-day employee experience - onboarding, wellbeing, recognition and feedback.",
    whyItFits: "You combine HR, design thinking and operational delivery.",
    startingSalary: "£40,000 - £50,000",
    midSalary: "£55,000 - £70,000",
    seniorSalary: "£80,000 - £100,000",
    timeToEntry: "3 - 5 Years HR / ops background",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong",
    weights: { people: 3, structure: 1, creativity: 1, income: 1, freedom: 1 },
    skillsNeeded: ["Employee research", "Programme design", "Communications", "Stakeholder management"],
  }),
  mk({
    name: "Diversity and Inclusion Manager",
    category: "Business, Finance and Legal",
    shortDescription: "Lead organisational diversity, equity and inclusion strategy - policy, programmes, measurement and reporting.",
    whyItFits: "You care about systemic change and are skilled at navigating organisational politics.",
    startingSalary: "£40,000 - £50,000",
    midSalary: "£55,000 - £70,000",
    seniorSalary: "£80,000 - £110,000",
    timeToEntry: "3 - 6 years HR / consulting background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong",
    weights: { people: 3, structure: 1, income: 1, creativity: 1, freedom: 0 },
    skillsNeeded: ["DEI strategy", "Communications", "Data literacy", "Influence"],
  }),
  mk({
    name: "Workplace Mediator",
    category: "Business, Finance and Legal",
    shortDescription: "Mediate workplace disputes and grievances to resolve conflict without escalation to tribunal.",
    whyItFits: "You're an exceptional listener who can hold a fair, neutral stance under pressure.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £110,000",
    timeToEntry: "1 - 2 years (CMC-accredited training)",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Client offices, online",
    stability: "Strong - workplace conflict rising",
    weights: { people: 3, structure: 1, income: 1, creativity: 0, freedom: 2 },
    skillsNeeded: ["Mediation", "Active listening", "Neutrality", "Note-taking"],
    selfEmployable: true,
  }),
  mk({
    name: "Change Management Consultant",
    category: "Business, Finance and Legal",
    shortDescription: "Help organisations land big changes - tech rollouts, restructures, mergers - with employees on board.",
    whyItFits: "You're calm in ambiguity and can move between exec board and frontline staff.",
    startingSalary: "£45,000 - £60,000",
    midSalary: "£70,000 - £100,000",
    seniorSalary: "£130,000 - £200,000+",
    timeToEntry: "3 - 6 years consulting background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Client sites, hybrid, travel",
    stability: "Strong",
    weights: { people: 3, structure: 2, income: 3, creativity: 1, freedom: 1 },
    skillsNeeded: ["Change frameworks (Prosci / ADKAR)", "Stakeholder mgmt", "Comms", "Project management"],
    selfEmployable: true,
  }),
  mk({
    name: "Training Designer",
    category: "Education, Science and Research",
    shortDescription: "Design self-paced and live training content - storyboards, scripts, e-learning modules and workshops.",
    whyItFits: "You're a creative who likes structure and communication design.",
    startingSalary: "£30,000 - £37,000",
    midSalary: "£42,000 - £55,000",
    seniorSalary: "£65,000 - £85,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote, hybrid",
    stability: "Strong",
    weights: { creativity: 3, structure: 2, freedom: 2, income: 0, people: 0 },
    skillsNeeded: ["Instructional design", "Articulate Storyline / Rise", "Storyboarding", "User testing"],
    selfEmployable: true,
  }),
];

// ---------------------------------------------------------------
// H. Media Production, Film, Audio & Entertainment (12)
// ---------------------------------------------------------------

const MEDIA_PRODUCTION: CareerSeed[] = [
  mk({
    name: "Sound Engineer",
    category: "Marketing and Media",
    shortDescription: "Capture, mix and master sound for music, film, TV, live events and podcasts.",
    whyItFits: "You're technical and musical in equal measure.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£30,000 - £40,000",
    seniorSalary: "£45,000 - £65,000+ (freelance higher)",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Studio, live venues, freelance",
    stability: "Variable - project work",
    weights: { creativity: 3, structure: 1, freedom: 1, people: 0, income: -1 },
    skillsNeeded: ["DAW expertise", "Microphone technique", "Mixing", "Problem solving"],
    selfEmployable: true,
  }),
  mk({
    name: "Music Producer",
    category: "Marketing and Media",
    shortDescription: "Shape recorded music - arranging, producing, mixing and helping artists realise their sound.",
    whyItFits: "You're a musician + producer hybrid who can lead a session and finish a track.",
    startingSalary: "£0 - £15,000 (year 1)",
    midSalary: "£25,000 - £60,000",
    seniorSalary: "£80,000 - £500,000+",
    timeToEntry: "3 - 7 years",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Studio, often nights",
    stability: "Highly variable",
    weights: { creativity: 3, freedom: 3, people: 1, structure: -1, income: 0 },
    skillsNeeded: ["DAW", "Arrangement", "Mixing", "Artist communication"],
    selfEmployable: true,
  }),
  mk({
    name: "Film Producer",
    category: "Marketing and Media",
    shortDescription: "Drive a film or TV project from script to screen - financing, casting, scheduling and delivery.",
    whyItFits: "You're a commercial creative who can hold the line on budgets and crew.",
    startingSalary: "£25,000 - £35,000 (runner / coordinator)",
    midSalary: "£50,000 - £90,000",
    seniorSalary: "£120,000 - £500,000+",
    timeToEntry: "5 - 10 years",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Production offices, set, festivals",
    stability: "Variable",
    weights: { people: 3, creativity: 2, income: 2, structure: 1, freedom: 1 },
    skillsNeeded: ["Production management", "Pitching", "Budgeting", "Relationship building"],
    selfEmployable: true,
  }),
  mk({
    name: "TV Researcher",
    category: "Marketing and Media",
    shortDescription: "Find stories, contributors and facts that bring TV and documentary programmes to life.",
    whyItFits: "You're naturally curious and a great phone person.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£30,000 - £40,000",
    seniorSalary: "£45,000 - £60,000",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Production company, hybrid",
    stability: "Variable - contract-based",
    weights: { creativity: 2, people: 2, freedom: 1, structure: 0, income: -1 },
    skillsNeeded: ["Research", "Casting", "Fact-checking", "Storytelling"],
  }),
  mk({
    name: "Broadcast Technician",
    category: "Marketing and Media",
    shortDescription: "Operate and maintain the technical kit behind live TV, radio and streaming - cameras, vision mixers, audio chains.",
    whyItFits: "You're technically minded and like working on shifts in live environments.",
    startingSalary: "£25,000 - £30,000",
    midSalary: "£32,000 - £42,000",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Studio, outside broadcast units, shifts",
    stability: "Strong",
    weights: { structure: 2, creativity: 1, people: 0, income: 0, freedom: 0 },
    skillsNeeded: ["Broadcast workflows", "Signal flow", "Fault-finding", "Calm under pressure"],
  }),
  mk({
    name: "Radio Producer",
    category: "Marketing and Media",
    shortDescription: "Produce live and pre-recorded radio - features, interviews, music sequences and podcasts.",
    whyItFits: "You're a storyteller who likes the discipline of broadcast.",
    startingSalary: "£25,000 - £32,000",
    midSalary: "£35,000 - £45,000",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Studio, hybrid",
    stability: "Variable",
    weights: { creativity: 3, people: 2, structure: 1, income: -1, freedom: 1 },
    skillsNeeded: ["Audio editing", "Interviewing", "Script writing", "Pacing"],
  }),
  mk({
    name: "Screenwriter",
    category: "Marketing and Media",
    shortDescription: "Write scripts for film, TV, streaming and short-form drama.",
    whyItFits: "You can sit alone and write for years and have stories you can't not tell.",
    startingSalary: "£0 - £10,000 (year 1)",
    midSalary: "£20,000 - £50,000",
    seniorSalary: "£60,000 - £500,000+ (top of WGA scale)",
    timeToEntry: "5+ years",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Home / writers' rooms",
    stability: "Highly variable",
    weights: { creativity: 3, freedom: 3, structure: -1, income: 0, people: 0 },
    skillsNeeded: ["Story structure", "Dialogue", "Self-direction", "Resilience"],
    selfEmploymentOnly: true,
  }),
  mk({
    name: "Camera Operator",
    category: "Marketing and Media",
    shortDescription: "Operate professional cameras on set, location, OB or studios - drama, factual, sport or news.",
    whyItFits: "You're a visual thinker who likes physical, technical work in fast-moving environments.",
    startingSalary: "£25,000 - £30,000",
    midSalary: "£35,000 - £45,000",
    seniorSalary: "£55,000 - £80,000+ (freelance day rates higher)",
    timeToEntry: "2 - 5 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Set, location, OB",
    stability: "Variable - freelance",
    weights: { creativity: 2, freedom: 2, structure: 0, people: 1, income: 0 },
    skillsNeeded: ["Camera operation", "Composition", "Lighting awareness", "Stamina"],
    selfEmployable: true,
  }),
  mk({
    name: "Set Designer",
    category: "Design and Creative",
    shortDescription: "Design sets for film, TV, theatre and events - drawings, models, build supervision.",
    whyItFits: "You're a 3D creative who likes hands-on construction.",
    startingSalary: "£25,000 - £32,000",
    midSalary: "£36,000 - £45,000",
    seniorSalary: "£55,000 - £80,000",
    timeToEntry: "3 - 5 Years",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Studios, workshops, location",
    stability: "Variable",
    weights: { creativity: 3, structure: 1, people: 1, freedom: 1, income: 0 },
    skillsNeeded: ["Drawing / CAD", "Model-making", "Materials", "Budget management"],
    selfEmployable: true,
  }),
  mk({
    name: "Production Manager",
    category: "Marketing and Media",
    shortDescription: "Run the production logistics of a film, TV show, ad or event - budgets, crews, schedules and contracts.",
    whyItFits: "You're a fixer who can hold lots of moving parts together.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £100,000",
    timeToEntry: "3 - 6 years production background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Production office, set",
    stability: "Variable - contract-based",
    weights: { people: 2, structure: 3, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Budgeting", "Scheduling", "Contracts", "Leadership"],
  }),
  mk({
    name: "Video Producer",
    category: "Marketing and Media",
    shortDescription: "Plan and produce video content for brands, agencies and creators - end-to-end ownership.",
    whyItFits: "You're a hybrid creative-producer who can shoot, edit and deliver.",
    startingSalary: "£30,000 - £38,000",
    midSalary: "£42,000 - £55,000",
    seniorSalary: "£65,000 - £85,000",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Hybrid - office, location",
    stability: "Strong - brand video demand",
    weights: { creativity: 3, people: 1, freedom: 1, structure: 1, income: 1 },
    skillsNeeded: ["Storyboarding", "Camera / audio basics", "Editing", "Project management"],
    selfEmployable: true,
  }),
  mk({
    name: "Voiceover Artist",
    category: "Marketing and Media",
    shortDescription: "Record commercial voiceover for ads, e-learning, video games, audiobooks and explainers.",
    whyItFits: "You have a recognisable voice and can perform from a home studio.",
    startingSalary: "£0 - £10,000 (year 1)",
    midSalary: "£20,000 - £55,000",
    seniorSalary: "£70,000 - £200,000+ (top artists)",
    timeToEntry: "1 - 3 years (training + showreel)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Extremely High",
    workEnvironment: "Home studio, remote sessions",
    stability: "Highly variable",
    weights: { creativity: 3, freedom: 3, structure: -1, people: 1, income: 0 },
    skillsNeeded: ["Vocal performance", "Self-direction", "Audio recording", "Marketing"],
    selfEmploymentOnly: true,
  }),
];

// ---------------------------------------------------------------
// I. Logistics, Supply Chain & International Trade (12)
// ---------------------------------------------------------------

const LOGISTICS: CareerSeed[] = [
  mk({
    name: "Supply Chain Analyst",
    category: "Logistics and Supply Chain",
    shortDescription: "Analyse and optimise the flow of goods, data and cash through a supply chain.",
    whyItFits: "You're a strong analytical thinker who likes operational, real-world data.",
    startingSalary: "£30,000 - £36,000",
    midSalary: "£42,000 - £55,000",
    seniorSalary: "£65,000 - £85,000",
    timeToEntry: "1 - 3 years (often grad scheme)",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong",
    weights: { structure: 2, income: 1, creativity: 0, people: 0, freedom: 0 },
    skillsNeeded: ["Excel / SQL", "Forecasting", "Process mapping", "Stakeholder mgmt"],
  }),
  mk({
    name: "Procurement Manager",
    category: "Logistics and Supply Chain",
    shortDescription: "Source and negotiate goods and services for an organisation - supplier strategy, contracts and savings.",
    whyItFits: "You're commercially sharp and like negotiation work with measurable impact.",
    startingSalary: "£40,000 - £50,000",
    midSalary: "£55,000 - £70,000",
    seniorSalary: "£85,000 - £120,000",
    timeToEntry: "3 - 6 years",
    degreeRequired: "Preferred - CIPS valued",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, supplier visits",
    stability: "Very strong",
    weights: { structure: 2, income: 2, people: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Negotiation", "Contracts", "Category strategy", "Supplier management"],
  }),
  mk({
    name: "Warehouse Manager",
    category: "Logistics and Supply Chain",
    shortDescription: "Lead a warehouse team - inbound, picking, packing, dispatch, safety and KPIs.",
    whyItFits: "You like leading operational teams and running a tight ship without a degree.",
    startingSalary: "£30,000 - £38,000",
    midSalary: "£40,000 - £48,000",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "3 - 5 Years operational experience",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Warehouse, shifts",
    stability: "Very strong",
    weights: { people: 2, structure: 2, income: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Team leadership", "WMS systems", "H&S", "KPI management"],
  }),
  mk({
    name: "Fleet Manager",
    category: "Logistics and Supply Chain",
    shortDescription: "Run a vehicle fleet - drivers, compliance, maintenance, fuel and route efficiency.",
    whyItFits: "You're a methodical operator who likes a mix of compliance and people management.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£45,000 - £55,000",
    seniorSalary: "£60,000 - £80,000",
    timeToEntry: "3 - 6 years (CPC Operator licence)",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Office + depot",
    stability: "Strong",
    weights: { structure: 3, people: 1, income: 1, creativity: -1, freedom: 0 },
    skillsNeeded: ["Operator CPC", "DVSA compliance", "Telematics", "Cost control"],
  }),
  mk({
    name: "Import Export Specialist",
    category: "Logistics and Supply Chain",
    shortDescription: "Manage customs, duty, documentation and shipping arrangements for international trade.",
    whyItFits: "You're detail-led with strong process skills and like global work.",
    startingSalary: "£28,000 - £34,000",
    midSalary: "£36,000 - £48,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Office",
    stability: "Strong - post-Brexit complexity",
    weights: { structure: 3, income: 1, people: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Customs / HMRC procedures", "Incoterms", "Documentation", "Languages helpful"],
  }),
  mk({
    name: "Customs Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Enforce UK customs and excise law on imports, exports and travellers - HMRC Border Force role.",
    whyItFits: "You want a uniformed enforcement role with progression and no degree barrier.",
    startingSalary: "£26,000 - £30,000",
    midSalary: "£32,000 - £38,000",
    seniorSalary: "£40,000 - £50,000",
    timeToEntry: "6 - 12 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Ports, airports, freight depots",
    stability: "Very strong",
    weights: { structure: 2, people: 1, income: 0, creativity: -1, freedom: 0 },
    skillsNeeded: ["Investigation", "Procedure", "Vigilance", "Communication"],
  }),
  mk({
    name: "Shipping Manager",
    category: "Logistics and Supply Chain",
    shortDescription: "Manage all outbound shipping - carriers, routes, costs and delivery performance.",
    whyItFits: "You're a logistics operator who likes a fast, KPI-driven environment.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£45,000 - £55,000",
    seniorSalary: "£60,000 - £80,000",
    timeToEntry: "3 - 5 Years logistics experience",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Office + warehouse",
    stability: "Strong",
    weights: { structure: 2, people: 1, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Carrier management", "Routing", "Cost analysis", "WMS / TMS"],
  }),
  mk({
    name: "Transport Planner",
    category: "Logistics and Supply Chain",
    shortDescription: "Plan transport routes, vehicle utilisation and driver shifts to maximise efficiency.",
    whyItFits: "You like puzzles - this is operational chess.",
    startingSalary: "£28,000 - £34,000",
    midSalary: "£37,000 - £48,000",
    seniorSalary: "£55,000 - £70,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Office / control room, shifts",
    stability: "Strong",
    weights: { structure: 3, people: 0, income: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Routing software", "Driver hours rules", "Communication", "Numeracy"],
  }),
  mk({
    name: "Freight Forwarder",
    category: "Logistics and Supply Chain",
    shortDescription: "Arrange international transport of goods across sea, air, road and rail for clients.",
    whyItFits: "You're a relationship-led problem-solver who likes international work.",
    startingSalary: "£26,000 - £32,000",
    midSalary: "£36,000 - £48,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "0 - 18 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office",
    stability: "Strong",
    weights: { structure: 2, people: 1, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Modes & routing", "Documentation", "Negotiation", "Customer service"],
  }),
  mk({
    name: "Inventory Manager",
    category: "Logistics and Supply Chain",
    shortDescription: "Own stock levels, forecasting, replenishment and shrinkage across one or many locations.",
    whyItFits: "You like numbers, accuracy, and reducing waste.",
    startingSalary: "£30,000 - £36,000",
    midSalary: "£40,000 - £48,000",
    seniorSalary: "£55,000 - £70,000",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Office + warehouse",
    stability: "Strong",
    weights: { structure: 3, income: 1, people: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Forecasting", "ERP systems", "Excel", "Process improvement"],
  }),
  mk({
    name: "Operations Analyst",
    category: "Logistics and Supply Chain",
    shortDescription: "Analyse and improve operational processes - service, cost, throughput and quality.",
    whyItFits: "You're analytical and like making operations measurably better.",
    startingSalary: "£32,000 - £38,000",
    midSalary: "£45,000 - £58,000",
    seniorSalary: "£65,000 - £85,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong",
    weights: { structure: 3, income: 1, creativity: 0, people: 0, freedom: 0 },
    skillsNeeded: ["Process mapping", "SQL / Excel", "Statistical analysis", "Stakeholder mgmt"],
  }),
  mk({
    name: "Supply Chain Director",
    category: "Logistics and Supply Chain",
    shortDescription: "Lead the entire supply chain function - strategy, network design, supplier relationships, transformation.",
    whyItFits: "You're a senior operator who wants to shape an organisation end-to-end.",
    startingSalary: "£75,000 - £95,000",
    midSalary: "£110,000 - £150,000",
    seniorSalary: "£180,000 - £350,000+",
    timeToEntry: "10+ years supply chain background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "HQ, travel to sites",
    stability: "Strong",
    weights: { people: 3, income: 3, structure: 2, creativity: 1, freedom: 1 },
    skillsNeeded: ["Strategy", "Leadership", "Transformation", "Commercial acumen"],
  }),
];

// ---------------------------------------------------------------
// J. Data, AI-Adjacent & Automation Careers (12)
// ---------------------------------------------------------------

const DATA_AI: CareerSeed[] = [
  // PRIORITY
  {
    name: "AI Prompt Engineer",
    category: "AI, Data and Automation",
    shortDescription:
      "Design, test and refine prompts and pipelines that get the most out of LLMs in production - eval, guardrails and fine-tuning included.",
    whyItFits:
      "You combine writing, software thinking and rigour - a new role that didn't exist 3 years ago.",
    startingSalary: "£35,000 - £50,000",
    midSalary: "£55,000 - £80,000",
    seniorSalary: "£90,000 - £140,000+",
    timeToEntry: "6 - 18 months",
    degreeRequired: "Preferred - portfolio matters more",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Remote, hybrid, fast-moving teams",
    stability: "Growing fast",
    weights: { structure: 1, creativity: 2, income: 2, freedom: 2, people: 0 },
    skillsNeeded: ["LLM internals", "Prompt design + evals", "Python", "Clear writing"],
    specialisations: [
      { title: "Applied Prompt Engineer (SaaS)", shortDescription: "Build and maintain LLM features inside production apps.", typicalEntryRoute: "Dev background + LLM portfolio", salaryRange: "£45,000 - £100,000" },
      { title: "AI Eval Specialist", shortDescription: "Design evaluation frameworks, golden sets and regression tests for LLM features.", typicalEntryRoute: "QA / data background + LLM exposure", salaryRange: "£50,000 - £110,000" },
      { title: "Conversation Designer", shortDescription: "Design conversational flows, personas and guardrails for AI products.", typicalEntryRoute: "UX writing background + LLM", salaryRange: "£40,000 - £85,000" },
    ],
  },
  mk({
    name: "AI Product Specialist",
    category: "AI, Data and Automation",
    shortDescription: "Specialist PM role focused on shipping AI features - from idea to eval to production.",
    whyItFits: "You're product-minded and want to ride the AI wave with real ownership.",
    startingSalary: "£45,000 - £60,000",
    midSalary: "£70,000 - £100,000",
    seniorSalary: "£120,000 - £180,000+",
    timeToEntry: "3 - 5 Years PM / data background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Remote, hybrid",
    stability: "Strong",
    weights: { people: 2, structure: 2, income: 3, creativity: 2, freedom: 1 },
    skillsNeeded: ["LLM product design", "Eval", "Prioritisation", "Stakeholder management"],
  }),
  mk({
    name: "AI Ethics Consultant",
    category: "AI, Data and Automation",
    shortDescription: "Advise organisations on responsible AI - bias, transparency, EU AI Act, governance and policy.",
    whyItFits: "You're a values-led thinker with research and writing skills.",
    startingSalary: "£40,000 - £55,000",
    midSalary: "£60,000 - £80,000",
    seniorSalary: "£95,000 - £150,000",
    timeToEntry: "3 - 5 Years",
    degreeRequired: "Preferred - Philosophy, Law, CS Background",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid, client visits",
    stability: "Strong - regulatory tailwind",
    weights: { structure: 2, people: 1, creativity: 1, income: 1, freedom: 1 },
    skillsNeeded: ["AI governance", "Risk assessment", "Stakeholder management", "Writing"],
    selfEmployable: true,
  }),
  // PRIORITY
  {
    name: "Automation Specialist",
    category: "AI, Data and Automation",
    shortDescription:
      "Build no-code / low-code automations and AI agents that replace manual back-office work across a business.",
    whyItFits:
      "You're a builder who loves removing pain from processes and can pick up new tools quickly.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £110,000+",
    timeToEntry: "6 - 18 months",
    degreeRequired: "Preferred - portfolio matters more",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote",
    stability: "Strong - large back-office cost-out budgets",
    weights: { structure: 2, income: 2, creativity: 2, freedom: 2, people: 0 },
    skillsNeeded: ["Zapier / Make / n8n", "Python basics", "Process design", "LLM integration"],
    specialisations: [
      { title: "RPA Developer", shortDescription: "Build robotic process automation in UiPath, Blue Prism or Power Automate.", typicalEntryRoute: "Dev background + RPA certs", salaryRange: "£40,000 - £85,000" },
      { title: "AI Workflow Engineer", shortDescription: "Stitch LLMs into business workflows via n8n, Make and APIs.", typicalEntryRoute: "Automation background + LLM portfolio", salaryRange: "£45,000 - £95,000" },
      { title: "Internal Tools Builder", shortDescription: "Ship internal tools with Retool, Airtable and Supabase.", typicalEntryRoute: "Ops or dev background + low-code portfolio", salaryRange: "£40,000 - £85,000" },
    ],
    selfEmployable: true,
  },
  mk({
    name: "CRM Specialist",
    category: "AI, Data and Automation",
    shortDescription: "Run CRM platforms (HubSpot, Salesforce, Klaviyo) - data, automation, lifecycle and reporting.",
    whyItFits: "You're a technical marketer who likes systems thinking.",
    startingSalary: "£30,000 - £37,000",
    midSalary: "£42,000 - £55,000",
    seniorSalary: "£65,000 - £85,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote, hybrid",
    stability: "Strong",
    weights: { structure: 2, income: 1, creativity: 1, people: 0, freedom: 1 },
    skillsNeeded: ["CRM admin", "Email marketing", "Segmentation", "SQL helpful"],
  }),
  mk({
    name: "RevOps Analyst",
    category: "AI, Data and Automation",
    shortDescription: "Operationalise revenue across sales, marketing and CS - data, tooling, attribution, forecasting.",
    whyItFits: "You're analytical with strong stakeholder skills across go-to-market.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £100,000",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote, hybrid",
    stability: "Strong in SaaS",
    weights: { structure: 2, income: 2, creativity: 0, people: 1, freedom: 1 },
    skillsNeeded: ["Salesforce / HubSpot", "SQL", "Forecasting", "Process design"],
  }),
  // PRIORITY
  {
    name: "Business Intelligence Analyst",
    category: "AI, Data and Automation",
    shortDescription:
      "Turn raw data into dashboards, models and insights that drive business decisions across teams.",
    whyItFits:
      "You're a data person with strong communication who likes work that gets used.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£48,000 - £62,000",
    seniorSalary: "£75,000 - £100,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, remote, hybrid",
    stability: "Strong - cross-sector demand",
    weights: { structure: 3, income: 2, creativity: 1, people: 0, freedom: 1 },
    skillsNeeded: ["SQL", "Tableau / Power BI / Looker", "Data modelling", "Communication"],
    specialisations: [
      { title: "Analytics Engineer", shortDescription: "Build modelled data layers with dbt for analyst self-service.", typicalEntryRoute: "BI + SQL/dbt portfolio", salaryRange: "£45,000 - £90,000" },
      { title: "Marketing BI Analyst", shortDescription: "Own dashboards for paid, lifecycle and attribution.", typicalEntryRoute: "Marketing analytics background", salaryRange: "£35,000 - £75,000" },
      { title: "Senior BI Manager", shortDescription: "Lead a BI team and own enterprise reporting strategy.", typicalEntryRoute: "Senior analyst + leadership", salaryRange: "£70,000 - £110,000" },
    ],
  },
  // PRIORITY
  {
    name: "Technical Writer",
    category: "AI, Data and Automation",
    shortDescription:
      "Write clear, accurate documentation - API references, developer guides, internal runbooks and user help.",
    whyItFits:
      "You're a clear writer who can read code and translate technical concepts for real people.",
    startingSalary: "£30,000 - £40,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£70,000 - £95,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote",
    stability: "Strong",
    weights: { structure: 2, creativity: 2, freedom: 2, income: 1, people: 0 },
    skillsNeeded: ["Plain English writing", "Markdown / Git", "API basics", "Information architecture"],
    specialisations: [
      { title: "API Documentation Writer", shortDescription: "Reference docs, SDK guides and tutorials for developer products.", typicalEntryRoute: "Tech writing + REST API fluency", salaryRange: "£40,000 - £80,000" },
      { title: "Developer Experience Writer", shortDescription: "Onboarding, sample apps and quickstarts that get devs to value fast.", typicalEntryRoute: "Tech writing + dev background", salaryRange: "£45,000 - £90,000" },
      { title: "Internal Documentation Lead", shortDescription: "Build and own internal documentation systems for engineering and ops.", typicalEntryRoute: "Senior tech writer + IA skills", salaryRange: "£55,000 - £90,000" },
    ],
    selfEmployable: true,
  },
  mk({
    name: "Solutions Consultant",
    category: "AI, Data and Automation",
    shortDescription: "Pre-sales technical role at a SaaS - demos, technical wins and onboarding for new customers.",
    whyItFits: "You combine commercial selling with hands-on technical depth.",
    startingSalary: "£45,000 - £60,000 + commission",
    midSalary: "£75,000 - £110,000 OTE",
    seniorSalary: "£140,000 - £220,000+ OTE",
    timeToEntry: "3 - 5 Years technical / sales background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Remote with client travel",
    stability: "Strong in SaaS",
    weights: { people: 3, income: 3, structure: 1, creativity: 1, freedom: 1 },
    skillsNeeded: ["Technical demos", "Discovery", "Solution design", "Storytelling"],
  }),
  mk({
    name: "Product Operations Manager",
    category: "AI, Data and Automation",
    shortDescription: "Run the operating system of a product team - rituals, tooling, data, research ops.",
    whyItFits: "You're a systems-thinker who likes scaling how product teams work.",
    startingSalary: "£45,000 - £55,000",
    midSalary: "£60,000 - £80,000",
    seniorSalary: "£90,000 - £130,000",
    timeToEntry: "3 - 6 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote, hybrid",
    stability: "Strong in larger product orgs",
    weights: { structure: 3, people: 2, income: 2, creativity: 1, freedom: 0 },
    skillsNeeded: ["Operations design", "Tooling", "Research ops", "Stakeholder mgmt"],
  }),
  mk({
    name: "Data Governance Analyst",
    category: "AI, Data and Automation",
    shortDescription: "Define and enforce policies on how data is captured, classified, retained and accessed.",
    whyItFits: "You like order, policy and the intersection of data and regulation.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£48,000 - £62,000",
    seniorSalary: "£70,000 - £90,000",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Office, hybrid",
    stability: "Very strong",
    weights: { structure: 3, income: 1, creativity: -1, people: 0, freedom: 0 },
    skillsNeeded: ["Data cataloguing", "GDPR", "Policy writing", "Stakeholder management"],
  }),
  mk({
    name: "Martech Specialist",
    category: "AI, Data and Automation",
    shortDescription: "Own the marketing tech stack - CDP, CRM, attribution, MA tools and integrations.",
    whyItFits: "You're technical and marketing literate.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£50,000 - £62,000",
    seniorSalary: "£70,000 - £95,000",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote, hybrid",
    stability: "Strong",
    weights: { structure: 2, income: 1, creativity: 1, people: 0, freedom: 1 },
    skillsNeeded: ["Marketing tools", "APIs", "Attribution", "SQL helpful"],
  }),
];

// ---------------------------------------------------------------
// K. Beauty, Aesthetics & Personal Services (10)
// ---------------------------------------------------------------

const BEAUTY: CareerSeed[] = [
  mk({
    name: "Nail Technician",
    category: "Beauty and Aesthetics",
    shortDescription: "Provide manicures, pedicures, gel and acrylic extensions in salons or as a mobile service.",
    whyItFits: "You're creative, detail-driven and want a self-employable trade with low entry costs.",
    startingSalary: "£18,000 - £24,000",
    midSalary: "£28,000 - £35,000",
    seniorSalary: "£40,000 - £55,000",
    timeToEntry: "3 - 9 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Salon, mobile, home studio",
    stability: "Steady local demand",
    weights: { creativity: 2, people: 2, freedom: 2, structure: 0, income: -1 },
    skillsNeeded: ["Technique", "Customer service", "Hygiene & sterilisation", "Booking management"],
    selfEmployable: true,
  }),
  mk({
    name: "Tattoo Artist",
    category: "Beauty and Aesthetics",
    shortDescription: "Design and apply custom tattoos for clients - usually after a long apprenticeship.",
    whyItFits: "You're a serious artist who can build a niche style and a loyal client list.",
    startingSalary: "£12,000 - £20,000 (apprentice)",
    midSalary: "£25,000 - £50,000",
    seniorSalary: "£60,000 - £150,000+",
    timeToEntry: "2 - 5 years apprenticeship",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Studio",
    stability: "Variable",
    weights: { creativity: 3, freedom: 3, income: 0, people: 1, structure: -1 },
    skillsNeeded: ["Drawing", "Tattoo technique", "Hygiene & safety", "Client consultation"],
    selfEmploymentOnly: true,
  }),
  mk({
    name: "Barber",
    category: "Beauty and Aesthetics",
    shortDescription: "Cut, style and shave hair for male clients - in shops or as a self-employed chair renter.",
    whyItFits: "You like creative trade work with strong local demand and quick wins.",
    startingSalary: "£20,000 - £25,000",
    midSalary: "£28,000 - £35,000",
    seniorSalary: "£40,000 - £55,000",
    timeToEntry: "1 - 2 years (NVQ / apprenticeship)",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Barbershop, mobile",
    stability: "Steady",
    weights: { creativity: 2, people: 3, freedom: 2, structure: 0, income: -1 },
    skillsNeeded: ["Cutting & styling", "Customer service", "Speed", "Hygiene"],
    selfEmployable: true,
  }),
  mk({
    name: "Makeup Artist",
    category: "Beauty and Aesthetics",
    shortDescription: "Apply makeup for weddings, events, editorial and film / TV clients.",
    whyItFits: "You're artistic, detail-driven and want a freelance-friendly creative career.",
    startingSalary: "£20,000 - £28,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£55,000 - £90,000+",
    timeToEntry: "6 - 18 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Mobile, studio, set",
    stability: "Variable",
    weights: { creativity: 3, freedom: 2, people: 2, income: 0, structure: 0 },
    skillsNeeded: ["Application technique", "Colour theory", "Hygiene", "Marketing"],
    selfEmployable: true,
  }),
  mk({
    name: "Lash Technician",
    category: "Beauty and Aesthetics",
    shortDescription: "Apply individual or volume lash extensions and lash lifts in salons or from a home studio.",
    whyItFits: "You're patient and precise and want a flexible self-employed beauty role.",
    startingSalary: "£18,000 - £24,000",
    midSalary: "£28,000 - £36,000",
    seniorSalary: "£40,000 - £55,000",
    timeToEntry: "1 - 6 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Salon, home studio",
    stability: "Steady",
    weights: { creativity: 1, people: 2, freedom: 2, structure: 0, income: -1 },
    skillsNeeded: ["Steady hand", "Hygiene", "Customer service", "Booking management"],
    selfEmployable: true,
  }),
  mk({
    name: "Aesthetic Practitioner",
    category: "Beauty and Aesthetics",
    shortDescription: "Deliver non-surgical aesthetic treatments - skincare, peels, microneedling, advanced facials.",
    whyItFits: "You combine beauty industry experience with a more clinical, well-paid career path.",
    startingSalary: "£30,000 - £38,000",
    midSalary: "£45,000 - £65,000",
    seniorSalary: "£75,000 - £130,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred - Level 4/5 aesthetics qualifications",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Clinic",
    stability: "Strong - growing market",
    weights: { people: 2, income: 2, creativity: 1, freedom: 1, structure: 1 },
    skillsNeeded: ["Skin science", "Consultation", "Hygiene", "Sales"],
    selfEmployable: true,
  }),
  mk({
    name: "Cosmetic Injector",
    category: "Beauty and Aesthetics",
    shortDescription: "Administer botox, dermal fillers and other injectables - a regulated, high-earning clinical role.",
    whyItFits: "You're a regulated healthcare professional (nurse / dentist / doctor) wanting a private aesthetic career.",
    startingSalary: "£40,000 - £55,000",
    midSalary: "£65,000 - £100,000",
    seniorSalary: "£130,000 - £300,000+",
    timeToEntry: "1 - 2 years post-NMC / GMC / GDC",
    degreeRequired: "Regulated - must be NMC / GMC / GDC registered",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Clinic",
    stability: "Strong",
    weights: { people: 2, income: 3, structure: 2, creativity: 0, freedom: 1 },
    skillsNeeded: ["Clinical injection", "Anatomy", "Consultation", "Sales"],
    selfEmployable: true,
  }),
  mk({
    name: "Skincare Specialist",
    category: "Beauty and Aesthetics",
    shortDescription: "Run skincare consultations and treatments and recommend personalised regimens to clients.",
    whyItFits: "You're a beauty-industry professional with a science bent and good people skills.",
    startingSalary: "£20,000 - £26,000",
    midSalary: "£30,000 - £40,000",
    seniorSalary: "£45,000 - £60,000",
    timeToEntry: "6 - 18 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Clinic, salon, retail",
    stability: "Steady",
    weights: { people: 2, creativity: 1, structure: 0, income: -1, freedom: 1 },
    skillsNeeded: ["Skin analysis", "Product knowledge", "Sales", "Consultation"],
    selfEmployable: true,
  }),
  mk({
    name: "Salon Owner",
    category: "Beauty and Aesthetics",
    shortDescription: "Own and run a hair / beauty / nails salon - hiring, scheduling, marketing, finances.",
    whyItFits: "You're a beauty professional who wants to build a real business.",
    startingSalary: "Variable - often £0 - £20,000 year one",
    midSalary: "£30,000 - £60,000",
    seniorSalary: "£75,000 - £200,000+ (multi-site)",
    timeToEntry: "3 - 5 Years industry experience",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Salon",
    stability: "Variable",
    weights: { people: 3, income: 1, freedom: 2, structure: 1, creativity: 1 },
    skillsNeeded: ["Operations", "Hiring", "Marketing", "Finance"],
    selfEmploymentOnly: true,
  }),
  mk({
    name: "Spa Manager",
    category: "Hospitality and Food",
    shortDescription: "Run a hotel or destination spa - therapists, treatments, retail, guest experience and revenue.",
    whyItFits: "You're a hospitality leader with wellness industry experience.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£40,000 - £50,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "3 - 6 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Hotel / spa",
    stability: "Strong in destination markets",
    weights: { people: 3, structure: 2, income: 1, creativity: 1, freedom: 0 },
    skillsNeeded: ["Leadership", "Treatments knowledge", "Revenue management", "Guest experience"],
  }),
];

// ---------------------------------------------------------------
// L. Maritime, Offshore & Specialist Physical Careers (10)
// ---------------------------------------------------------------

const MARITIME: CareerSeed[] = [
  mk({
    name: "Offshore Engineer",
    category: "Maritime and Offshore",
    shortDescription: "Design and maintain offshore oil, gas and wind installations - mechanical, electrical or subsea.",
    whyItFits: "You want serious engineering work, high pay and the rota lifestyle.",
    startingSalary: "£45,000 - £55,000",
    midSalary: "£65,000 - £90,000",
    seniorSalary: "£110,000 - £180,000+",
    timeToEntry: "3 - 5 Years (degree + offshore training)",
    degreeRequired: "Preferred - engineering degree",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Offshore platforms, vessels, rota",
    stability: "Strong",
    weights: { structure: 2, income: 3, freedom: 1, people: 0, creativity: 0 },
    skillsNeeded: ["Engineering fundamentals", "Offshore safety", "Problem solving", "Stamina"],
  }),
  mk({
    name: "Marine Engineer",
    category: "Maritime and Offshore",
    shortDescription: "Design, build and maintain ships and shipboard systems - engines, propulsion, electrics.",
    whyItFits: "You want hands-on engineering with travel.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £100,000",
    timeToEntry: "3 - 5 Years (cadetship / degree)",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Onboard ships, shipyards, design offices",
    stability: "Strong",
    weights: { structure: 2, income: 2, freedom: 1, people: 0, creativity: 0 },
    skillsNeeded: ["Marine systems", "MEOL / STCW certification", "Diagnosis", "Project management"],
  }),
  mk({
    name: "Merchant Navy Officer",
    category: "Maritime and Offshore",
    shortDescription: "Deck or engineering officer on commercial cargo, tanker or passenger vessels.",
    whyItFits: "You want a regulated maritime career with travel and well-paid progression.",
    startingSalary: "£30,000 - £40,000 (cadet -> 3rd Officer)",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£70,000 - £100,000+",
    timeToEntry: "3 - 4 years (cadetship)",
    degreeRequired: "Preferred - sponsored cadetship route",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Vessels, long rotations at sea",
    stability: "Strong",
    weights: { structure: 2, income: 2, freedom: 1, people: 1, creativity: 0 },
    skillsNeeded: ["STCW", "Navigation / engineering", "Leadership", "Resilience"],
  }),
  mk({
    name: "Ship Captain",
    category: "Maritime and Offshore",
    shortDescription: "Master of a commercial vessel - ultimate responsibility for ship, crew and cargo.",
    whyItFits: "You're at the top of the deck officer pipeline and want full command.",
    startingSalary: "£45,000 - £60,000",
    midSalary: "£70,000 - £90,000",
    seniorSalary: "£100,000 - £180,000+",
    timeToEntry: "10+ years progression",
    degreeRequired: "Regulated - Master's CoC required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Vessels, long rotations at sea",
    stability: "Strong",
    weights: { people: 2, income: 3, structure: 2, creativity: 0, freedom: 1 },
    skillsNeeded: ["Leadership", "Navigation", "Regulatory", "Decision under pressure"],
  }),
  // PRIORITY
  {
    name: "Underwater Welder",
    category: "Maritime and Offshore",
    shortDescription:
      "Carry out specialist welding on subsea pipelines, ship hulls and offshore platforms - one of the highest-paid trades in the world.",
    whyItFits:
      "You're a qualified welder ready to add commercial diving and accept high-risk, high-pay work.",
    startingSalary: "£50,000 - £65,000",
    midSalary: "£70,000 - £100,000",
    seniorSalary: "£120,000 - £200,000+",
    timeToEntry: "1 - 3 years (commercial dive school after welder qual)",
    degreeRequired: "Not required - HSE Part I / IMCA diving certs",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Offshore, subsea, rota work",
    stability: "Variable - tied to oil & gas / renewables",
    weights: { freedom: 1, income: 3, structure: 1, people: 0, creativity: 0 },
    skillsNeeded: ["Welding (TIG/MMA)", "Commercial diving", "Subsea engineering", "Safety mindset"],
    specialisations: [
      { title: "Inshore Welder Diver", shortDescription: "Subsea welding in UK waters - harbours, locks, oil terminals.", typicalEntryRoute: "HSE SCUBA + Surface Supplied + welding ticket", salaryRange: "£40,000 - £80,000" },
      { title: "Offshore Saturation Welder Diver", shortDescription: "Saturation dives at depth on North Sea oil and gas.", typicalEntryRoute: "Closed-bell training + 5+ years subsea", salaryRange: "£90,000 - £200,000+" },
      { title: "Renewables Subsea Specialist", shortDescription: "Inspection and welding on offshore wind subsea cables and foundations.", typicalEntryRoute: "Diving background + offshore wind experience", salaryRange: "£70,000 - £150,000" },
    ],
  },
  // PRIORITY
  {
    name: "Offshore Wind Technician",
    category: "Maritime and Offshore",
    shortDescription:
      "Install and maintain offshore wind turbines - boat or helicopter transfer, well-paid rota work.",
    whyItFits:
      "You're hands-on, OK with offshore life, and want to be part of the UK's offshore wind boom.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £100,000+",
    timeToEntry: "12 - 24 months (apprenticeship + GWO + sea survival)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Offshore wind farms, rota",
    stability: "Very strong - record UK offshore pipeline",
    weights: { freedom: 1, income: 2, structure: 1, people: 0, creativity: 0 },
    skillsNeeded: ["Electrical & mechanical maintenance", "Working at height", "Sea survival", "Hydraulics"],
    specialisations: [
      { title: "Commissioning Technician", shortDescription: "Bring brand-new turbines online during construction phase.", typicalEntryRoute: "Technician + commissioning training", salaryRange: "£45,000 - £75,000" },
      { title: "Service Technician", shortDescription: "Maintain operating wind farms across multiple sites.", typicalEntryRoute: "Apprenticeship + GWO", salaryRange: "£35,000 - £60,000" },
      { title: "Subsea Cable Specialist", shortDescription: "Inspect and repair inter-array and export cables.", typicalEntryRoute: "Cable specialism + offshore tickets", salaryRange: "£60,000 - £100,000" },
    ],
  },
  mk({
    name: "Marine Surveyor",
    category: "Maritime and Offshore",
    shortDescription: "Inspect ships, cargo, yachts or offshore structures for insurers, owners and regulators.",
    whyItFits: "You're a maritime professional ready to move into a senior, advisory role.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£75,000 - £100,000",
    timeToEntry: "5+ years maritime background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Ports, vessels, travel",
    stability: "Strong",
    weights: { structure: 2, income: 2, freedom: 1, people: 1, creativity: 0 },
    skillsNeeded: ["Ship knowledge", "Reporting", "Regulation", "Diagnosis"],
    selfEmployable: true,
  }),
  mk({
    name: "Port Operations Manager",
    category: "Maritime and Offshore",
    shortDescription: "Run day-to-day operations at a port or terminal - berthing, cargo, safety and customer service.",
    whyItFits: "You're a port / logistics professional ready for senior leadership.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£48,000 - £60,000",
    seniorSalary: "£70,000 - £95,000",
    timeToEntry: "5+ years operational background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Low",
    workEnvironment: "Port / terminal, shifts",
    stability: "Strong",
    weights: { people: 2, structure: 2, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Leadership", "Maritime regulation", "Logistics", "H&S"],
  }),
  mk({
    name: "Fisheries Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Enforce fisheries law, inspect vessels and protect marine ecosystems for the Marine Management Organisation.",
    whyItFits: "You want outdoor public-service work tied to the marine environment.",
    startingSalary: "£25,000 - £30,000",
    midSalary: "£32,000 - £38,000",
    seniorSalary: "£40,000 - £50,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Ports, vessels, coast",
    stability: "Strong",
    weights: { structure: 2, freedom: 1, people: 1, creativity: 0, income: -1 },
    skillsNeeded: ["Fisheries law", "Inspection", "Investigation", "Report writing"],
  }),
  mk({
    name: "Rope Access Technician",
    category: "Maritime and Offshore",
    shortDescription: "Carry out maintenance, inspection or NDT at height using rope access (IRATA) on bridges, wind turbines and buildings.",
    whyItFits: "You're a hands-on tradesperson who's comfortable at height and wants high day rates.",
    startingSalary: "£30,000 - £38,000",
    midSalary: "£42,000 - £55,000",
    seniorSalary: "£60,000 - £85,000+",
    timeToEntry: "1 - 3 years (IRATA Level 1-3)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "At height - bridges, turbines, structures",
    stability: "Strong",
    weights: { freedom: 1, income: 2, structure: 1, people: 0, creativity: 0 },
    skillsNeeded: ["IRATA technique", "Trade skill (paint / inspection / welding)", "Working at height", "Risk assessment"],
    selfEmployable: true,
  }),
];

// ---------------------------------------------------------------
// M. Extra Under-Served Professional Careers (10)
// ---------------------------------------------------------------

const EXTRA_PROFESSIONAL: CareerSeed[] = [
  mk({
    name: "Tax Advisor",
    category: "Business, Finance and Legal",
    shortDescription: "Advise individuals and businesses on UK tax planning, compliance and HMRC enquiries.",
    whyItFits: "You're detail-driven, like rules-based problem-solving and want strong earning potential.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£50,000 - £70,000",
    seniorSalary: "£80,000 - £150,000+",
    timeToEntry: "3 - 5 Years (CTA / ATT)",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Very strong",
    weights: { structure: 3, income: 2, people: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["UK tax law", "Analytical thinking", "Communication", "Software literacy"],
    selfEmployable: true,
  }),
  mk({
    name: "Pension Advisor",
    category: "Business, Finance and Legal",
    shortDescription: "Advise clients on pensions, transfers, drawdown and retirement income planning.",
    whyItFits: "You're a regulated advisor who likes long-term, life-stage work.",
    startingSalary: "£30,000 - £40,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£70,000 - £100,000",
    timeToEntry: "1 - 3 years (Level 4 + AF7 for pension transfers)",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, client visits",
    stability: "Strong",
    weights: { people: 2, structure: 2, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Pension rules", "Fact finding", "Suitability writing", "Regulation"],
    selfEmployable: true,
  }),
  // PRIORITY
  {
    name: "Business Development Manager",
    category: "Business, Finance and Legal",
    shortDescription:
      "Open new commercial opportunities for an organisation - new logos, partnerships, channels and revenue.",
    whyItFits:
      "You're a senior sales operator who likes hunting work, not account management.",
    startingSalary: "£35,000 - £45,000 + commission",
    midSalary: "£55,000 - £75,000 OTE",
    seniorSalary: "£100,000 - £180,000+ OTE",
    timeToEntry: "3 - 6 years sales background",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Hybrid - office, client visits, travel",
    stability: "Cyclical with sector",
    weights: { people: 3, income: 3, freedom: 1, creativity: 1, structure: 0 },
    skillsNeeded: ["Prospecting", "Discovery", "Negotiation", "Account planning"],
    specialisations: [
      { title: "SaaS BDM", shortDescription: "New-logo sales in software / SaaS.", typicalEntryRoute: "SDR -> AE -> BDM progression", salaryRange: "£60,000 - £160,000 OTE" },
      { title: "Enterprise BDM", shortDescription: "Long-cycle 6-7 figure deals into large customers.", typicalEntryRoute: "Senior AE with vertical expertise", salaryRange: "£100,000 - £250,000 OTE" },
      { title: "Channel / Partnership BDM", shortDescription: "Build revenue through resellers, SI and tech partnerships.", typicalEntryRoute: "Sales + partnership background", salaryRange: "£70,000 - £150,000 OTE" },
    ],
  },
  mk({
    name: "Bid Manager",
    category: "Business, Finance and Legal",
    shortDescription: "Lead complex tenders and proposals - own the bid response from kickoff to submission.",
    whyItFits: "You're a writer-strategist who can wrangle technical and commercial teams to deadline.",
    startingSalary: "£40,000 - £48,000",
    midSalary: "£55,000 - £70,000",
    seniorSalary: "£80,000 - £110,000",
    timeToEntry: "3 - 5 Years bid / proposal background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong - public-sector demand",
    weights: { structure: 3, people: 2, income: 1, creativity: 1, freedom: 0 },
    skillsNeeded: ["Bid writing", "Storyboarding", "Stakeholder mgmt", "Deadline management"],
  }),
  mk({
    name: "Grants Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Write, manage and report on grants for charities, councils or research institutions.",
    whyItFits: "You're a writer who likes mission-driven work and rigorous compliance.",
    startingSalary: "£28,000 - £33,000",
    midSalary: "£36,000 - £42,000",
    seniorSalary: "£45,000 - £55,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong - sustained sector demand",
    weights: { structure: 2, people: 1, creativity: 1, income: -1, freedom: 0 },
    skillsNeeded: ["Grant writing", "Budgeting", "Compliance", "Stakeholder communication"],
  }),
  mk({
    name: "Fundraiser",
    category: "Public Sector and Emergency Services",
    shortDescription: "Raise income for a charity through major donors, trusts, events, corporate or community giving.",
    whyItFits: "You're a relationship-led storyteller who cares about a cause.",
    startingSalary: "£25,000 - £32,000",
    midSalary: "£38,000 - £48,000",
    seniorSalary: "£55,000 - £80,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, events, donor visits",
    stability: "Strong",
    weights: { people: 3, creativity: 1, income: 0, structure: 1, freedom: 1 },
    skillsNeeded: ["Storytelling", "Pitching", "CRM", "Event management"],
  }),
  mk({
    name: "Policy Advisor",
    category: "Public Sector and Emergency Services",
    shortDescription: "Develop and assess policy options for government, regulators, charities or think tanks.",
    whyItFits: "You're analytical, write well and want to influence how the country works.",
    startingSalary: "£32,000 - £40,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£65,000 - £95,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office, hybrid (Civil Service or think tank)",
    stability: "Very strong",
    weights: { structure: 2, creativity: 1, people: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Policy analysis", "Briefing writing", "Stakeholder management", "Research"],
  }),
  // PRIORITY
  {
    name: "UX Writer",
    category: "Design and Creative",
    shortDescription:
      "Write the words inside digital products - buttons, errors, empty states and onboarding flows.",
    whyItFits:
      "You're a clear, deliberate writer who likes design thinking and tiny-but-important decisions.",
    startingSalary: "£35,000 - £42,000",
    midSalary: "£48,000 - £62,000",
    seniorSalary: "£70,000 - £100,000+",
    timeToEntry: "1 - 3 years (UX or content background)",
    degreeRequired: "Preferred - portfolio matters more",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Remote, hybrid product teams",
    stability: "Strong in larger product orgs",
    weights: { creativity: 2, structure: 2, freedom: 2, people: 1, income: 1 },
    skillsNeeded: ["Plain English", "UX research basics", "Design systems", "Figma fluency"],
    specialisations: [
      { title: "Conversational UX Writer", shortDescription: "Write for chatbots, voice products and AI assistants.", typicalEntryRoute: "UX writing + LLM exposure", salaryRange: "£45,000 - £90,000" },
      { title: "Content Designer", shortDescription: "Own content end-to-end across user journeys, not just microcopy.", typicalEntryRoute: "Senior UX writing", salaryRange: "£50,000 - £95,000" },
      { title: "Localisation UX Writer", shortDescription: "Adapt product copy across markets and languages.", typicalEntryRoute: "UX writing + localisation experience", salaryRange: "£40,000 - £80,000" },
    ],
  },
  // PRIORITY
  {
    name: "Service Designer",
    category: "Design and Creative",
    shortDescription:
      "Design end-to-end services across digital and human touchpoints - GOV.UK-style and beyond.",
    whyItFits:
      "You're a systems-minded designer who can hold complex journeys in your head and on the wall.",
    startingSalary: "£40,000 - £50,000",
    midSalary: "£55,000 - £75,000",
    seniorSalary: "£85,000 - £120,000+",
    timeToEntry: "3 - 5 Years design / research background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid - Civil Service or consultancy",
    stability: "Very strong - public-sector and enterprise demand",
    weights: { people: 2, structure: 2, creativity: 2, income: 1, freedom: 1 },
    skillsNeeded: ["Journey mapping", "Workshop facilitation", "Service blueprints", "Research synthesis"],
    specialisations: [
      { title: "Public Sector Service Designer", shortDescription: "Design government services to GOV.UK Service Standard.", typicalEntryRoute: "GDS-style hiring + portfolio", salaryRange: "£50,000 - £90,000" },
      { title: "Health Service Designer", shortDescription: "Redesign patient pathways in NHS or private healthcare.", typicalEntryRoute: "Service design + health context", salaryRange: "£55,000 - £100,000" },
      { title: "Enterprise Service Designer", shortDescription: "Reshape internal services - HR, IT, customer ops.", typicalEntryRoute: "Senior service designer in consultancy", salaryRange: "£65,000 - £110,000" },
    ],
    selfEmployable: true,
  },
  mk({
    name: "Product Marketing Manager",
    category: "Marketing and Media",
    shortDescription: "Position, launch and grow software products - messaging, sales enablement, pricing and competitive intel.",
    whyItFits: "You combine commercial thinking, customer empathy and storytelling.",
    startingSalary: "£45,000 - £55,000",
    midSalary: "£65,000 - £85,000",
    seniorSalary: "£100,000 - £160,000+",
    timeToEntry: "4 - 7 years marketing background",
    degreeRequired: "Preferred",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Remote, hybrid",
    stability: "Strong in SaaS",
    weights: { creativity: 2, people: 2, income: 2, structure: 1, freedom: 1 },
    skillsNeeded: ["Positioning", "Messaging", "Sales enablement", "Research"],
  }),
];

// ---------------------------------------------------------------
// Aggregate export
// ---------------------------------------------------------------

export const CAREER_EXPANSION_SEEDS: CareerSeed[] = [
  ...AVIATION,
  ...SECURITY_COMPLIANCE,
  ...MENTAL_HEALTH,
  ...CREATOR_ECONOMY,
  ...GREEN_ENERGY,
  ...LUXURY_WEALTH,
  ...HUMAN_BEHAVIOUR,
  ...MEDIA_PRODUCTION,
  ...LOGISTICS,
  ...DATA_AI,
  ...BEAUTY,
  ...MARITIME,
  ...EXTRA_PROFESSIONAL,
];
